import { SkillsManagementClient as api } from "../apiClients/SkillsManagementClient";
export const addTagTypes = [
  "Users",
  "Auth",
  "Permissions",
  "Postes",
  "Roles",
  "Audit",
  "Companies",
  "Disciplines",
  "Units",
  "Skills",
  "Evaluations",
  "Skill Poste Requirements",
  "Rater Types",
  "Practical Evidences",
  "Quizzes",
  "Training Recommendations",
  "Competency Gaps",
  "Competency Reports",
  "Course Skill Mappings",
  "Certificates",
  "Certification Types",
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      loginClientUser: build.mutation<
        LoginClientUserApiResponse,
        LoginClientUserApiArg
      >({
        query: (queryArg) => ({
          url: `/users/login`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Users"],
      }),
      createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
        query: (queryArg) => ({
          url: `/users/create-user`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Users"],
      }),
      getUsers: build.query<GetUsersApiResponse, GetUsersApiArg>({
        query: (queryArg) => ({
          url: `/users`,
          params: {
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            type: queryArg["type"],
            isArchived: queryArg.isArchived,
          },
        }),
        providesTags: ["Users"],
      }),
      getLiteUsers: build.query<GetLiteUsersApiResponse, GetLiteUsersApiArg>({
        query: (queryArg) => ({
          url: `/users/lite`,
          params: {
            filters: queryArg.filters,
            search: queryArg.search,
            limit: queryArg.limit,
          },
        }),
        providesTags: ["Users"],
      }),
      getUser: build.query<GetUserApiResponse, GetUserApiArg>({
        query: (queryArg) => ({
          url: `/users/${queryArg.userId}`,
          params: {
            type: queryArg["type"],
          },
        }),
        providesTags: ["Users"],
      }),
      deleteUser: build.mutation<DeleteUserApiResponse, DeleteUserApiArg>({
        query: (queryArg) => ({
          url: `/users/${queryArg.userId}`,
          method: "DELETE",
          params: {
            force: queryArg.force,
            type: queryArg["type"],
          },
        }),
        invalidatesTags: ["Users"],
      }),
      updateUser: build.mutation<UpdateUserApiResponse, UpdateUserApiArg>({
        query: (queryArg) => ({
          url: `/users/update`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Users"],
      }),
      putUsersUpdateByUserId: build.mutation<
        PutUsersUpdateByUserIdApiResponse,
        PutUsersUpdateByUserIdApiArg
      >({
        query: (queryArg) => ({
          url: `/users/update/${queryArg.userId}`,
          method: "PUT",
          body: queryArg.body,
          params: {
            type: queryArg["type"],
          },
        }),
        invalidatesTags: ["Users"],
      }),
      refreshToken: build.mutation<RefreshTokenApiResponse, RefreshTokenApiArg>(
        {
          query: () => ({ url: `/users/refresh-token`, method: "POST" }),
          invalidatesTags: ["Auth"],
        },
      ),
      logout: build.mutation<LogoutApiResponse, LogoutApiArg>({
        query: () => ({ url: `/users/logout`, method: "POST" }),
        invalidatesTags: ["Auth"],
      }),
      requestPasswordReset: build.mutation<
        RequestPasswordResetApiResponse,
        RequestPasswordResetApiArg
      >({
        query: (queryArg) => ({
          url: `/users/request-password-reset`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Users"],
      }),
      resetPassword: build.mutation<
        ResetPasswordApiResponse,
        ResetPasswordApiArg
      >({
        query: (queryArg) => ({
          url: `/users/reset-password`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Users"],
      }),
      changePassword: build.mutation<
        ChangePasswordApiResponse,
        ChangePasswordApiArg
      >({
        query: (queryArg) => ({
          url: `/users/change-password`,
          method: "POST",
          body: queryArg.changePasswordSchema,
        }),
        invalidatesTags: ["Users"],
      }),
      getPermissions: build.query<
        GetPermissionsApiResponse,
        GetPermissionsApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/available`,
          params: {
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Permissions"],
      }),
      assignMultiplePermissions: build.mutation<
        AssignMultiplePermissionsApiResponse,
        AssignMultiplePermissionsApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/assign-permissions/${queryArg.userId}`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Permissions"],
      }),
      removePermission: build.mutation<
        RemovePermissionApiResponse,
        RemovePermissionApiArg
      >({
        query: (queryArg) => ({
          url: `/permissions/${queryArg.userId}/${queryArg.permissionId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Permissions"],
      }),
      getPermissionSets: build.query<
        GetPermissionSetsApiResponse,
        GetPermissionSetsApiArg
      >({
        query: () => ({ url: `/permissions/permission-sets` }),
        providesTags: ["Permissions"],
      }),
      getPostes: build.query<GetPostesApiResponse, GetPostesApiArg>({
        query: (queryArg) => ({
          url: `/postes`,
          params: {
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            getAllWithoutPagination: queryArg.getAllWithoutPagination,
          },
        }),
        providesTags: ["Postes"],
      }),
      createPoste: build.mutation<CreatePosteApiResponse, CreatePosteApiArg>({
        query: (queryArg) => ({
          url: `/postes/create`,
          method: "POST",
          body: queryArg.posteCreateSchema,
        }),
        invalidatesTags: ["Postes"],
      }),
      createPosteWithRequirements: build.mutation<
        CreatePosteWithRequirementsApiResponse,
        CreatePosteWithRequirementsApiArg
      >({
        query: (queryArg) => ({
          url: `/postes/create-with-requirements`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Postes"],
      }),
      getPoste: build.query<GetPosteApiResponse, GetPosteApiArg>({
        query: (queryArg) => ({ url: `/postes/${queryArg.posteId}` }),
        providesTags: ["Postes"],
      }),
      updatePoste: build.mutation<UpdatePosteApiResponse, UpdatePosteApiArg>({
        query: (queryArg) => ({
          url: `/postes/${queryArg.posteId}`,
          method: "PATCH",
          body: queryArg.posteUpdateSchema,
        }),
        invalidatesTags: ["Postes"],
      }),
      deletePoste: build.mutation<DeletePosteApiResponse, DeletePosteApiArg>({
        query: (queryArg) => ({
          url: `/postes/${queryArg.posteId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Postes"],
      }),
      assignPosteToUser: build.mutation<
        AssignPosteToUserApiResponse,
        AssignPosteToUserApiArg
      >({
        query: (queryArg) => ({
          url: `/postes/assign/${queryArg.posteId}/${queryArg.userId}`,
          method: "POST",
        }),
        invalidatesTags: ["Postes", "Users"],
      }),
      assignPosteToDiscipline: build.mutation<
        AssignPosteToDisciplineApiResponse,
        AssignPosteToDisciplineApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/disciplines/${queryArg.disciplineId}/assign-poste/${queryArg.posteId}`,
          method: "POST",
        }),
        invalidatesTags: ["Postes"],
      }),
      getRoles: build.query<GetRolesApiResponse, GetRolesApiArg>({
        query: (queryArg) => ({
          url: `/roles`,
          params: {
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Roles"],
      }),
      getRole: build.query<GetRoleApiResponse, GetRoleApiArg>({
        query: (queryArg) => ({ url: `/roles/${queryArg.roleId}` }),
        providesTags: ["Roles"],
      }),
      updateRole: build.mutation<UpdateRoleApiResponse, UpdateRoleApiArg>({
        query: (queryArg) => ({
          url: `/roles/${queryArg.roleId}`,
          method: "PUT",
          body: queryArg.roleUpdateSchema,
        }),
        invalidatesTags: ["Roles"],
      }),
      deleteRole: build.mutation<DeleteRoleApiResponse, DeleteRoleApiArg>({
        query: (queryArg) => ({
          url: `/roles/${queryArg.roleId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Roles"],
      }),
      assignRolesToUsers: build.mutation<
        AssignRolesToUsersApiResponse,
        AssignRolesToUsersApiArg
      >({
        query: (queryArg) => ({
          url: `/roles/assign-users`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Roles"],
      }),
      createRole: build.mutation<CreateRoleApiResponse, CreateRoleApiArg>({
        query: (queryArg) => ({
          url: `/roles/create`,
          method: "POST",
          body: queryArg.roleCreateSchema,
        }),
        invalidatesTags: ["Roles"],
      }),
      assignPermissionsToRole: build.mutation<
        AssignPermissionsToRoleApiResponse,
        AssignPermissionsToRoleApiArg
      >({
        query: (queryArg) => ({
          url: `/roles/permissions/${queryArg.roleId}`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Roles"],
      }),
      getAuditLogs: build.query<GetAuditLogsApiResponse, GetAuditLogsApiArg>({
        query: (queryArg) => ({
          url: `/audit`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            sortBy: queryArg.sortBy,
            sortOrder: queryArg.sortOrder,
            model_name: queryArg.modelName,
            action: queryArg.action,
            actor_user_id: queryArg.actorUserId,
            entity_id: queryArg.entityId,
            module: queryArg["module"],
            success: queryArg.success,
            request_id: queryArg.requestId,
            date_from: queryArg.dateFrom,
            date_to: queryArg.dateTo,
            search: queryArg.search,
          },
        }),
        providesTags: ["Audit"],
      }),
      getAuditLogById: build.query<
        GetAuditLogByIdApiResponse,
        GetAuditLogByIdApiArg
      >({
        query: (queryArg) => ({ url: `/audit/${queryArg.id}` }),
        providesTags: ["Audit"],
      }),
      deleteAuditLogById: build.mutation<
        DeleteAuditLogByIdApiResponse,
        DeleteAuditLogByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/audit/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Audit"],
      }),
      bulkDeleteAuditLogs: build.mutation<
        BulkDeleteAuditLogsApiResponse,
        BulkDeleteAuditLogsApiArg
      >({
        query: (queryArg) => ({
          url: `/audit/bulk-delete`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Audit"],
      }),
      getCompanies: build.query<GetCompaniesApiResponse, GetCompaniesApiArg>({
        query: (queryArg) => ({
          url: `/companies`,
          params: {
            isArchived: queryArg.isArchived,
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            search: queryArg.search,
          },
        }),
        providesTags: ["Companies"],
      }),
      createCompany: build.mutation<
        CreateCompanyApiResponse,
        CreateCompanyApiArg
      >({
        query: (queryArg) => ({
          url: `/companies`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Companies"],
      }),
      getCompany: build.query<GetCompanyApiResponse, GetCompanyApiArg>({
        query: (queryArg) => ({
          url: `/companies/${queryArg.companyId}`,
          params: {
            isArchived: queryArg.isArchived,
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            search: queryArg.search,
          },
        }),
        providesTags: ["Companies"],
      }),
      updateCompany: build.mutation<
        UpdateCompanyApiResponse,
        UpdateCompanyApiArg
      >({
        query: (queryArg) => ({
          url: `/companies/${queryArg.companyId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Companies"],
      }),
      deleteCompany: build.mutation<
        DeleteCompanyApiResponse,
        DeleteCompanyApiArg
      >({
        query: (queryArg) => ({
          url: `/companies/${queryArg.companyId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Companies"],
      }),
      archiveCompany: build.mutation<
        ArchiveCompanyApiResponse,
        ArchiveCompanyApiArg
      >({
        query: (queryArg) => ({
          url: `/companies/${queryArg.companyId}/archive`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Companies"],
      }),
      activateCompany: build.mutation<
        ActivateCompanyApiResponse,
        ActivateCompanyApiArg
      >({
        query: (queryArg) => ({
          url: `/companies/${queryArg.companyId}/activate`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Companies"],
      }),
      getCompanyEmployees: build.query<
        GetCompanyEmployeesApiResponse,
        GetCompanyEmployeesApiArg
      >({
        query: (queryArg) => ({
          url: `/companies/${queryArg.companyId}/employees`,
          params: {
            isArchived: queryArg.isArchived,
            filters: queryArg.filters,
            search: queryArg.search,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Companies"],
      }),
      getDisciplines: build.query<
        GetDisciplinesApiResponse,
        GetDisciplinesApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/disciplines`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            search: queryArg.search,
          },
        }),
        providesTags: ["Disciplines"],
      }),
      createDiscipline: build.mutation<
        CreateDisciplineApiResponse,
        CreateDisciplineApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/disciplines`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Disciplines"],
      }),
      getDisciplineById: build.query<
        GetDisciplineByIdApiResponse,
        GetDisciplineByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/disciplines/${queryArg.disciplineId}`,
        }),
        providesTags: ["Disciplines"],
      }),
      updateDiscipline: build.mutation<
        UpdateDisciplineApiResponse,
        UpdateDisciplineApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/disciplines/${queryArg.disciplineId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Disciplines"],
      }),
      deleteDiscipline: build.mutation<
        DeleteDisciplineApiResponse,
        DeleteDisciplineApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/disciplines/${queryArg.disciplineId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Disciplines"],
      }),
      getUnits: build.query<GetUnitsApiResponse, GetUnitsApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/units`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            search: queryArg.search,
            discipline_id: queryArg.disciplineId,
          },
        }),
        providesTags: ["Units"],
      }),
      createUnit: build.mutation<CreateUnitApiResponse, CreateUnitApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/units`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Units"],
      }),
      addSkillToUnits: build.mutation<
        AddSkillToUnitsApiResponse,
        AddSkillToUnitsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/units/add-skill`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Units"],
      }),
      getUnitById: build.query<GetUnitByIdApiResponse, GetUnitByIdApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/units/${queryArg.unitId}`,
        }),
        providesTags: ["Units"],
      }),
      updateUnit: build.mutation<UpdateUnitApiResponse, UpdateUnitApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/units/${queryArg.unitId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Units"],
      }),
      deleteUnit: build.mutation<DeleteUnitApiResponse, DeleteUnitApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/units/${queryArg.unitId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Units"],
      }),
      getUnitsByDiscipline: build.query<
        GetUnitsByDisciplineApiResponse,
        GetUnitsByDisciplineApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/units/discipline/${queryArg.disciplineId}`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            search: queryArg.search,
          },
        }),
        providesTags: ["Units"],
      }),
      getSkills: build.query<GetSkillsApiResponse, GetSkillsApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/skills`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            search: queryArg.search,
            unit_id: queryArg.unitId,
            priority: queryArg.priority,
            getAllWithoutPagination: queryArg.getAllWithoutPagination,
          },
        }),
        providesTags: ["Skills"],
      }),
      createSkill: build.mutation<CreateSkillApiResponse, CreateSkillApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/skills`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Skills"],
      }),
      getSkillById: build.query<GetSkillByIdApiResponse, GetSkillByIdApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/skills/${queryArg.skillId}`,
        }),
        providesTags: ["Skills"],
      }),
      updateSkill: build.mutation<UpdateSkillApiResponse, UpdateSkillApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/skills/${queryArg.skillId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Skills"],
      }),
      deleteSkill: build.mutation<DeleteSkillApiResponse, DeleteSkillApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/skills/${queryArg.skillId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Skills"],
      }),
      getSkillsByUnit: build.query<
        GetSkillsByUnitApiResponse,
        GetSkillsByUnitApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skills/unit/${queryArg.unitId}`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
            search: queryArg.search,
            priority: queryArg.priority,
          },
        }),
        providesTags: ["Skills"],
      }),
      getAllEvaluations: build.query<
        GetAllEvaluationsApiResponse,
        GetAllEvaluationsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations`,
          params: {
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Evaluations"],
      }),
      createEvaluation: build.mutation<
        CreateEvaluationApiResponse,
        CreateEvaluationApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/create`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Evaluations"],
      }),
      bulkCreateEvaluations: build.mutation<
        BulkCreateEvaluationsApiResponse,
        BulkCreateEvaluationsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/bulk`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Evaluations"],
      }),
      getEvaluation: build.query<GetEvaluationApiResponse, GetEvaluationApiArg>(
        {
          query: (queryArg) => ({
            url: `/skills-management/evaluations/${queryArg.evaluationId}`,
          }),
          providesTags: ["Evaluations"],
        },
      ),
      updateEvaluation: build.mutation<
        UpdateEvaluationApiResponse,
        UpdateEvaluationApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/${queryArg.evaluationId}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["Evaluations"],
      }),
      deleteEvaluation: build.mutation<
        DeleteEvaluationApiResponse,
        DeleteEvaluationApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/${queryArg.evaluationId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Evaluations"],
      }),
      getEmployeeEvaluations: build.query<
        GetEmployeeEvaluationsApiResponse,
        GetEmployeeEvaluationsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/employee/${queryArg.employeeId}`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Evaluations"],
      }),
      getSkillEvaluations: build.query<
        GetSkillEvaluationsApiResponse,
        GetSkillEvaluationsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/skill/${queryArg.skillId}`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Evaluations"],
      }),
      hasEvaluationsForEmployee: build.query<
        HasEvaluationsForEmployeeApiResponse,
        HasEvaluationsForEmployeeApiArg
      >({
        query: (queryArg) => ({ url: `/employee/:employeeId/has-evaluations` }),
        providesTags: ["Evaluations"],
      }),
      getSkillPosteRequirements: build.query<
        GetSkillPosteRequirementsApiResponse,
        GetSkillPosteRequirementsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements`,
          params: {
            filters: queryArg.filters,
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Skill Poste Requirements"],
      }),
      createSkillPosteRequirement: build.mutation<
        CreateSkillPosteRequirementApiResponse,
        CreateSkillPosteRequirementApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Skill Poste Requirements"],
      }),
      getSkillPosteRequirement: build.query<
        GetSkillPosteRequirementApiResponse,
        GetSkillPosteRequirementApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements/${queryArg.skillPosteId}`,
        }),
        providesTags: ["Skill Poste Requirements"],
      }),
      updateSkillPosteRequirement: build.mutation<
        UpdateSkillPosteRequirementApiResponse,
        UpdateSkillPosteRequirementApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements/${queryArg.skillPosteId}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["Skill Poste Requirements"],
      }),
      deleteSkillPosteRequirement: build.mutation<
        DeleteSkillPosteRequirementApiResponse,
        DeleteSkillPosteRequirementApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements/${queryArg.skillPosteId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Skill Poste Requirements"],
      }),
      getRequirementsBySkill: build.query<
        GetRequirementsBySkillApiResponse,
        GetRequirementsBySkillApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements/skill/${queryArg.skillId}`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Skill Poste Requirements"],
      }),
      getRequirementsByPoste: build.query<
        GetRequirementsByPosteApiResponse,
        GetRequirementsByPosteApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements/poste/${queryArg.posteId}`,
          params: {
            pagination: queryArg.pagination,
            sorting: queryArg.sorting,
          },
        }),
        providesTags: ["Skill Poste Requirements"],
      }),
      assignRequirementsToPoste: build.mutation<
        AssignRequirementsToPosteApiResponse,
        AssignRequirementsToPosteApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/skill-poste-requirements/assign/${queryArg.posteId}`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Skill Poste Requirements"],
      }),
      getRaterTypes: build.query<GetRaterTypesApiResponse, GetRaterTypesApiArg>(
        {
          query: (queryArg) => ({
            url: `/skills-management/rater-types`,
            params: {
              filters: queryArg.filters,
              pagination: queryArg.pagination,
              sorting: queryArg.sorting,
            },
          }),
          providesTags: ["Rater Types"],
        },
      ),
      createRaterType: build.mutation<
        CreateRaterTypeApiResponse,
        CreateRaterTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/rater-types`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Rater Types"],
      }),
      getRaterType: build.query<GetRaterTypeApiResponse, GetRaterTypeApiArg>({
        query: (queryArg) => ({
          url: `/skills-management/rater-types/${queryArg.id}`,
        }),
        providesTags: ["Rater Types"],
      }),
      updateRaterType: build.mutation<
        UpdateRaterTypeApiResponse,
        UpdateRaterTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/rater-types/${queryArg.id}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Rater Types"],
      }),
      deleteRaterType: build.mutation<
        DeleteRaterTypeApiResponse,
        DeleteRaterTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/rater-types/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Rater Types"],
      }),
      listPracticalEvidences: build.query<
        ListPracticalEvidencesApiResponse,
        ListPracticalEvidencesApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences`,
          params: {
            currentPage: queryArg.currentPage,
            rowsPerPage: queryArg.rowsPerPage,
            column: queryArg.column,
            direction: queryArg.direction,
            evaluation_id: queryArg.evaluationId,
            type: queryArg["type"],
            employee_id: queryArg.employeeId,
          },
        }),
        providesTags: ["Practical Evidences"],
      }),
      createPracticalEvidence: build.mutation<
        CreatePracticalEvidenceApiResponse,
        CreatePracticalEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Practical Evidences"],
      }),
      getPracticalEvidence: build.query<
        GetPracticalEvidenceApiResponse,
        GetPracticalEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences/${queryArg.evidenceId}`,
        }),
        providesTags: ["Practical Evidences"],
      }),
      updatePracticalEvidence: build.mutation<
        UpdatePracticalEvidenceApiResponse,
        UpdatePracticalEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences/${queryArg.evidenceId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Practical Evidences"],
      }),
      deletePracticalEvidence: build.mutation<
        DeletePracticalEvidenceApiResponse,
        DeletePracticalEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences/${queryArg.evidenceId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Practical Evidences"],
      }),
      listEvaluationEvidences: build.query<
        ListEvaluationEvidencesApiResponse,
        ListEvaluationEvidencesApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/${queryArg.evaluationId}/evidences`,
        }),
        providesTags: ["Practical Evidences"],
      }),
      createEvaluationEvidence: build.mutation<
        CreateEvaluationEvidenceApiResponse,
        CreateEvaluationEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/${queryArg.evaluationId}/evidences`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Practical Evidences"],
      }),
      uploadEvaluationEvidence: build.mutation<
        UploadEvaluationEvidenceApiResponse,
        UploadEvaluationEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/evaluations/${queryArg.evaluationId}/evidences/upload`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Practical Evidences"],
      }),
      approvePracticalEvidence: build.mutation<
        ApprovePracticalEvidenceApiResponse,
        ApprovePracticalEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences/${queryArg.evidenceId}/approve`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Practical Evidences"],
      }),
      rejectPracticalEvidence: build.mutation<
        RejectPracticalEvidenceApiResponse,
        RejectPracticalEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences/${queryArg.evidenceId}/reject`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Practical Evidences"],
      }),
      downloadPracticalEvidence: build.query<
        DownloadPracticalEvidenceApiResponse,
        DownloadPracticalEvidenceApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/practical-evidences/${queryArg.evidenceId}/download`,
        }),
        providesTags: ["Practical Evidences"],
      }),
      getQuizzes: build.query<GetQuizzesApiResponse, GetQuizzesApiArg>({
        query: (queryArg) => ({
          url: `/quizzes`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            search: queryArg.search,
            createdBy: queryArg.createdBy,
            posteId: queryArg.posteId,
            isActive: queryArg.isActive,
            minTimeLimit: queryArg.minTimeLimit,
            maxTimeLimit: queryArg.maxTimeLimit,
            minMaxAttempts: queryArg.minMaxAttempts,
            maxMaxAttempts: queryArg.maxMaxAttempts,
          },
        }),
        providesTags: ["Quizzes"],
      }),
      createQuiz: build.mutation<CreateQuizApiResponse, CreateQuizApiArg>({
        query: (queryArg) => ({
          url: `/quizzes`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getQuizById: build.query<GetQuizByIdApiResponse, GetQuizByIdApiArg>({
        query: (queryArg) => ({ url: `/quizzes/${queryArg.id}` }),
        providesTags: ["Quizzes"],
      }),
      updateQuiz: build.mutation<UpdateQuizApiResponse, UpdateQuizApiArg>({
        query: (queryArg) => ({
          url: `/quizzes/${queryArg.id}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      deleteQuiz: build.mutation<DeleteQuizApiResponse, DeleteQuizApiArg>({
        query: (queryArg) => ({
          url: `/quizzes/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      createQuizAssignment: build.mutation<
        CreateQuizAssignmentApiResponse,
        CreateQuizAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getQuizAssignments: build.query<
        GetQuizAssignmentsApiResponse,
        GetQuizAssignmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            quizId: queryArg.quizId,
            assigneeUserId: queryArg.assigneeUserId,
            assignedByUserId: queryArg.assignedByUserId,
            status: queryArg.status,
            dueBefore: queryArg.dueBefore,
            dueAfter: queryArg.dueAfter,
            expBefore: queryArg.expBefore,
            expAfter: queryArg.expAfter,
          },
        }),
        providesTags: ["Quizzes"],
      }),
      getQuizAssignmentById: build.query<
        GetQuizAssignmentByIdApiResponse,
        GetQuizAssignmentByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments/${queryArg.assignmentId}`,
        }),
        providesTags: ["Quizzes"],
      }),
      updateQuizAssignment: build.mutation<
        UpdateQuizAssignmentApiResponse,
        UpdateQuizAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments/${queryArg.assignmentId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      deleteQuizAssignment: build.mutation<
        DeleteQuizAssignmentApiResponse,
        DeleteQuizAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments/${queryArg.assignmentId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      acceptQuizAssignment: build.mutation<
        AcceptQuizAssignmentApiResponse,
        AcceptQuizAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments/${queryArg.assignmentId}/accept`,
          method: "POST",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      declineQuizAssignment: build.mutation<
        DeclineQuizAssignmentApiResponse,
        DeclineQuizAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments/${queryArg.assignmentId}/decline`,
          method: "POST",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      revokeQuizAssignment: build.mutation<
        RevokeQuizAssignmentApiResponse,
        RevokeQuizAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/assignments/${queryArg.assignmentId}/revoke`,
          method: "POST",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getQuizQuestions: build.query<
        GetQuizQuestionsApiResponse,
        GetQuizQuestionsApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/${queryArg.id}/quiz-questions`,
        }),
        providesTags: ["Quizzes"],
      }),
      getQuizSkills: build.query<GetQuizSkillsApiResponse, GetQuizSkillsApiArg>(
        {
          query: (queryArg) => ({ url: `/quizzes/${queryArg.id}/skills` }),
          providesTags: ["Quizzes"],
        },
      ),
      startQuizAttempt: build.mutation<
        StartQuizAttemptApiResponse,
        StartQuizAttemptApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/attempts/start`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      submitQuizAttempt: build.mutation<
        SubmitQuizAttemptApiResponse,
        SubmitQuizAttemptApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/attempts/${queryArg.attemptId}/submit`,
          method: "POST",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      evaluateQuizAttempt: build.mutation<
        EvaluateQuizAttemptApiResponse,
        EvaluateQuizAttemptApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/attempts/${queryArg.attemptId}/evaluate`,
          method: "POST",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getResponsesForAttempt: build.query<
        GetResponsesForAttemptApiResponse,
        GetResponsesForAttemptApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/attempts/${queryArg.attemptId}/responses`,
        }),
        providesTags: ["Quizzes"],
      }),
      saveAttemptResponse: build.mutation<
        SaveAttemptResponseApiResponse,
        SaveAttemptResponseApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/attempts/${queryArg.attemptId}/responses`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getAttempts: build.query<GetAttemptsApiResponse, GetAttemptsApiArg>({
        query: (queryArg) => ({
          url: `/quizzes/attempts`,
          params: {
            quizId: queryArg.quizId,
            page: queryArg.page,
            limit: queryArg.limit,
            userId: queryArg.userId,
            status: queryArg.status,
            minScore: queryArg.minScore,
            maxScore: queryArg.maxScore,
            minTimeSpent: queryArg.minTimeSpent,
            maxTimeSpent: queryArg.maxTimeSpent,
          },
        }),
        providesTags: ["Quizzes"],
      }),
      updateAttemptResponse: build.mutation<
        UpdateAttemptResponseApiResponse,
        UpdateAttemptResponseApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/responses/${queryArg.responseId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getAllQuizResponses: build.query<
        GetAllQuizResponsesApiResponse,
        GetAllQuizResponsesApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/responses`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            quizId: queryArg.quizId,
            userId: queryArg.userId,
            attemptId: queryArg.attemptId,
            questionId: queryArg.questionId,
            isCorrect: queryArg.isCorrect,
            textResponse: queryArg.textResponse,
          },
        }),
        providesTags: ["Quizzes"],
      }),
      createQuizQuestion: build.mutation<
        CreateQuizQuestionApiResponse,
        CreateQuizQuestionApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/quiz-questions`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getQuizzesQuestions: build.query<
        GetQuizzesQuestionsApiResponse,
        GetQuizzesQuestionsApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/quiz-questions`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            quizId: queryArg.quizId,
            skillId: queryArg.skillId,
            questionType: queryArg.questionType,
            isRequired: queryArg.isRequired,
            minPoints: queryArg.minPoints,
            maxPoints: queryArg.maxPoints,
            search: queryArg.search,
          },
        }),
        providesTags: ["Quizzes"],
      }),
      getQuizQuestionById: build.query<
        GetQuizQuestionByIdApiResponse,
        GetQuizQuestionByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/quiz-questions/${queryArg.id}`,
        }),
        providesTags: ["Quizzes"],
      }),
      updateQuizQuestion: build.mutation<
        UpdateQuizQuestionApiResponse,
        UpdateQuizQuestionApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/quiz-questions/${queryArg.id}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      deleteQuizQuestion: build.mutation<
        DeleteQuizQuestionApiResponse,
        DeleteQuizQuestionApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/quiz-questions/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      createQuestionOption: build.mutation<
        CreateQuestionOptionApiResponse,
        CreateQuestionOptionApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/question-options`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      getQuestionOptions: build.query<
        GetQuestionOptionsApiResponse,
        GetQuestionOptionsApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/question-options`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            questionId: queryArg.questionId,
            isCorrect: queryArg.isCorrect,
            search: queryArg.search,
          },
        }),
        providesTags: ["Quizzes"],
      }),
      getQuestionOptionById: build.query<
        GetQuestionOptionByIdApiResponse,
        GetQuestionOptionByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/question-options/${queryArg.id}`,
        }),
        providesTags: ["Quizzes"],
      }),
      updateQuestionOption: build.mutation<
        UpdateQuestionOptionApiResponse,
        UpdateQuestionOptionApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/question-options/${queryArg.id}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Quizzes"],
      }),
      deleteQuestionOption: build.mutation<
        DeleteQuestionOptionApiResponse,
        DeleteQuestionOptionApiArg
      >({
        query: (queryArg) => ({
          url: `/quizzes/question-options/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Quizzes"],
      }),
      listTrainingRecommendations: build.query<
        ListTrainingRecommendationsApiResponse,
        ListTrainingRecommendationsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/training-recommendations`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            gap_id: queryArg.gapId,
            status: queryArg.status,
            priority: queryArg.priority,
            training_type: queryArg.trainingType,
            courseId: queryArg.courseId,
            sessionId: queryArg.sessionId,
          },
        }),
        providesTags: ["Training Recommendations"],
      }),
      createTrainingRecommendation: build.mutation<
        CreateTrainingRecommendationApiResponse,
        CreateTrainingRecommendationApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/training-recommendations`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Training Recommendations"],
      }),
      getTrainingRecommendation: build.query<
        GetTrainingRecommendationApiResponse,
        GetTrainingRecommendationApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/training-recommendations/${queryArg.recommendationId}`,
        }),
        providesTags: ["Training Recommendations"],
      }),
      updateTrainingRecommendation: build.mutation<
        UpdateTrainingRecommendationApiResponse,
        UpdateTrainingRecommendationApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/training-recommendations/${queryArg.recommendationId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Training Recommendations"],
      }),
      deleteTrainingRecommendation: build.mutation<
        DeleteTrainingRecommendationApiResponse,
        DeleteTrainingRecommendationApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/training-recommendations/${queryArg.recommendationId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Training Recommendations"],
      }),
      listCompetencyGapsForEmployeePoste: build.query<
        ListCompetencyGapsForEmployeePosteApiResponse,
        ListCompetencyGapsForEmployeePosteApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/employees/${queryArg.employeeId}/postes/${queryArg.posteId}/gaps`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            skill_id: queryArg.skillId,
            status: queryArg.status,
          },
        }),
        providesTags: ["Competency Gaps"],
      }),
      recomputeCompetencyGapsForEmployeePoste: build.mutation<
        RecomputeCompetencyGapsForEmployeePosteApiResponse,
        RecomputeCompetencyGapsForEmployeePosteApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/employees/${queryArg.employeeId}/postes/${queryArg.posteId}/gaps/recompute`,
          method: "POST",
        }),
        invalidatesTags: ["Competency Gaps"],
      }),
      getPosteCompetencyMatrix: build.query<
        GetPosteCompetencyMatrixApiResponse,
        GetPosteCompetencyMatrixApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/competency-matrix`,
          params: {
            posteId: queryArg.posteId,
            employeeIds: queryArg.employeeIds,
            fallbackRequiredMin: queryArg.fallbackRequiredMin,
            fallbackRequiredMax: queryArg.fallbackRequiredMax,
            showAcceptedOnly: queryArg.showAcceptedOnly,
          },
        }),
        providesTags: ["Competency Gaps"],
      }),
      listCompetencyReports: build.query<
        ListCompetencyReportsApiResponse,
        ListCompetencyReportsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/competency-reports`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            report_type: queryArg.reportType,
            subject_type: queryArg.subjectType,
            subject_id: queryArg.subjectId,
            is_scheduled: queryArg.isScheduled,
            schedule_frequency: queryArg.scheduleFrequency,
          },
        }),
        providesTags: ["Competency Reports"],
      }),
      createCompetencyReport: build.mutation<
        CreateCompetencyReportApiResponse,
        CreateCompetencyReportApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/competency-reports`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Competency Reports"],
      }),
      getCompetencyReport: build.query<
        GetCompetencyReportApiResponse,
        GetCompetencyReportApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/competency-reports/${queryArg.reportId}`,
        }),
        providesTags: ["Competency Reports"],
      }),
      updateCompetencyReport: build.mutation<
        UpdateCompetencyReportApiResponse,
        UpdateCompetencyReportApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/competency-reports/${queryArg.reportId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Competency Reports"],
      }),
      deleteCompetencyReport: build.mutation<
        DeleteCompetencyReportApiResponse,
        DeleteCompetencyReportApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/competency-reports/${queryArg.reportId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Competency Reports"],
      }),
      listCourseSkillMappings: build.query<
        ListCourseSkillMappingsApiResponse,
        ListCourseSkillMappingsApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/course-skill-mappings`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            courseId: queryArg.courseId,
            skill_id: queryArg.skillId,
            coverage_level: queryArg.coverageLevel,
          },
        }),
        providesTags: ["Course Skill Mappings"],
      }),
      createCourseSkillMapping: build.mutation<
        CreateCourseSkillMappingApiResponse,
        CreateCourseSkillMappingApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/course-skill-mappings`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Course Skill Mappings"],
      }),
      getCourseSkillMapping: build.query<
        GetCourseSkillMappingApiResponse,
        GetCourseSkillMappingApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/course-skill-mappings/${queryArg.mappingId}`,
        }),
        providesTags: ["Course Skill Mappings"],
      }),
      updateCourseSkillMapping: build.mutation<
        UpdateCourseSkillMappingApiResponse,
        UpdateCourseSkillMappingApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/course-skill-mappings/${queryArg.mappingId}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Course Skill Mappings"],
      }),
      deleteCourseSkillMapping: build.mutation<
        DeleteCourseSkillMappingApiResponse,
        DeleteCourseSkillMappingApiArg
      >({
        query: (queryArg) => ({
          url: `/skills-management/course-skill-mappings/${queryArg.mappingId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Course Skill Mappings"],
      }),
      listAllCertifications: build.query<
        ListAllCertificationsApiResponse,
        ListAllCertificationsApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            certificationName: queryArg.certificationName,
            certificationTypeId: queryArg.certificationTypeId,
            candidateId: queryArg.candidateId,
            expirationState: queryArg.expirationState,
            sortBy: queryArg.sortBy,
            sortOrder: queryArg.sortOrder,
          },
        }),
        providesTags: ["Certificates"],
      }),
      createCertificate: build.mutation<
        CreateCertificateApiResponse,
        CreateCertificateApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Certificates"],
      }),
      getCertificateById: build.query<
        GetCertificateByIdApiResponse,
        GetCertificateByIdApiArg
      >({
        query: (queryArg) => ({ url: `/certificates/${queryArg.id}` }),
        providesTags: ["Certificates"],
      }),
      updateCertificate: build.mutation<
        UpdateCertificateApiResponse,
        UpdateCertificateApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates/${queryArg.id}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Certificates"],
      }),
      deleteCertificate: build.mutation<
        DeleteCertificateApiResponse,
        DeleteCertificateApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Certificates"],
      }),
      listAllCertificationTypes: build.query<
        ListAllCertificationTypesApiResponse,
        ListAllCertificationTypesApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates/certification-types`,
          params: {
            page: queryArg.page,
            limit: queryArg.limit,
            name: queryArg.name,
            category: queryArg.category,
            issuer: queryArg.issuer,
            sortBy: queryArg.sortBy,
            sortOrder: queryArg.sortOrder,
          },
        }),
        providesTags: ["Certification Types"],
      }),
      createCertificationType: build.mutation<
        CreateCertificationTypeApiResponse,
        CreateCertificationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates/certification-types`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Certification Types"],
      }),
      getCertificationTypeById: build.query<
        GetCertificationTypeByIdApiResponse,
        GetCertificationTypeByIdApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates/certification-types/${queryArg.id}`,
        }),
        providesTags: ["Certification Types"],
      }),
      updateCertificationType: build.mutation<
        UpdateCertificationTypeApiResponse,
        UpdateCertificationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates/certification-types/${queryArg.id}`,
          method: "PUT",
          body: queryArg.body,
        }),
        invalidatesTags: ["Certification Types"],
      }),
      deleteCertificationType: build.mutation<
        DeleteCertificationTypeApiResponse,
        DeleteCertificationTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/certificates/certification-types/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Certification Types"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as SkillsManagementClientApi };
export type LoginClientUserApiResponse = /** status 200 Login successful */ {
  token: string;
  user: {
    /** Username (required) */
    username?: string;
    /** Poste ID for the user (optional) */
    posteId?: number | null;
    /** User email address (required) */
    email: string;
    /** Role ID for the user (required) */
    roleId: number;
    /** Role object for the user (optional) */
    role?: RoleOutputSchema | null;
    /** Array of permission IDs (optional) */
    permissions?: number[] | null;
    /** User profile information (optional) */
    profile?: {
      /** User first name (required) */
      firstName: string;
      /** User last name (required) */
      lastName: string;
      /** User phone number (optional) */
      phone?: string | null;
      /** User birthday (optional) */
      birthday?: string | null;
      /** User gender (optional) */
      gender?: ("Male" | "Female") | null;
      /** User biography (optional) */
      bio?: string | null;
    } | null;
    /** Unique identifier for the user */
    id: string;
    /** User creation timestamp */
    createdAt: string;
    /** User last update timestamp */
    updatedAt: string;
    /** Evaluations for this user */
    evaluations?: {
      evaluation_id: number;
      employee_id: number;
      skill_id: number;
      evaluation_date: string;
      score: number;
      level?: string | null;
      comments?: string | null;
      evaluation_type?:
        | ("oral" | "written" | "practical")
        | ("oral" | "written" | "practical");
      rater_type: "Self-assessment" | "Manager" | "Peer";
      evaluatedBy?: null;
      createdAt: string;
      updatedAt: string;
      skill?: {
        skill_id: number;
        skill_code: string;
        name: string;
        description?: string | null;
        priority: "low" | "medium" | "high";
        unit_id: number;
        comments?: string | null;
        awareness_description?: string | null;
        skilled_application_description?: string | null;
        management_description?: string | null;
        internal_reference?: string | null;
        external_reference?: string | null;
        createdAt: string;
        updatedAt: string;
      };
    }[];
  };
};
export type LoginClientUserApiArg = {
  body: {
    /** User email address (required) */
    email: string;
    /** User password (required) */
    password: string;
    /** Source of login attempt (required) */
    loginSource: string;
  };
};
export type CreateUserApiResponse =
  /** status 201 User created successfully */ {
    message: string;
    user: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
  };
export type CreateUserApiArg = {
  body: {
    /** Username (required) */
    username?: string;
    /** Mot de passe explicite pour l'utilisateur (optionnel) */
    authPassword: string;
    /** Poste ID for the user (optional) */
    posteId?: number | null;
    /** User email address (required) */
    email: string;
    /** Role ID for the user (required) */
    roleId: number;
    /** Role object for the user (optional) */
    role?: RoleOutputSchema | null;
    /** Array of permission IDs (optional) */
    permissions?: number[] | null;
    /** User profile information (optional) */
    profile?: {
      /** User first name (required) */
      firstName: string;
      /** User last name (required) */
      lastName: string;
      /** User phone number (optional) */
      phone?: string | null;
      /** User birthday (optional) */
      birthday?: string | null;
      /** User gender (optional) */
      gender?: ("Male" | "Female") | null;
      /** User biography (optional) */
      bio?: string | null;
    } | null;
  };
};
export type GetUsersApiResponse = /** status 200 List of users */ {
  users: {
    /** Username (required) */
    username?: string;
    /** Poste ID for the user (optional) */
    posteId?: number | null;
    /** User email address (required) */
    email: string;
    /** Role ID for the user (required) */
    roleId: number;
    /** Role object for the user (optional) */
    role?: RoleOutputSchema | null;
    /** Array of permission IDs (optional) */
    permissions?: number[] | null;
    /** User profile information (optional) */
    profile?: {
      /** User first name (required) */
      firstName: string;
      /** User last name (required) */
      lastName: string;
      /** User phone number (optional) */
      phone?: string | null;
      /** User birthday (optional) */
      birthday?: string | null;
      /** User gender (optional) */
      gender?: ("Male" | "Female") | null;
      /** User biography (optional) */
      bio?: string | null;
    } | null;
    /** Unique identifier for the user */
    id: string;
    /** User creation timestamp */
    createdAt: string;
    /** User last update timestamp */
    updatedAt: string;
    /** Evaluations for this user */
    evaluations?: {
      evaluation_id: number;
      employee_id: number;
      skill_id: number;
      evaluation_date: string;
      score: number;
      level?: string | null;
      comments?: string | null;
      evaluation_type?:
        | ("oral" | "written" | "practical")
        | ("oral" | "written" | "practical");
      rater_type: "Self-assessment" | "Manager" | "Peer";
      evaluatedBy?: null;
      createdAt: string;
      updatedAt: string;
      skill?: {
        skill_id: number;
        skill_code: string;
        name: string;
        description?: string | null;
        priority: "low" | "medium" | "high";
        unit_id: number;
        comments?: string | null;
        awareness_description?: string | null;
        skilled_application_description?: string | null;
        management_description?: string | null;
        internal_reference?: string | null;
        external_reference?: string | null;
        createdAt: string;
        updatedAt: string;
      };
    }[];
  }[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
  };
};
export type GetUsersApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  /** Filter by user type,in client api we dont need it,in agent api its agent by default if the endpoint doesnt send any query */
  type?: "user";
  isArchived?: true | false;
};
export type GetLiteUsersApiResponse =
  /** status 200 Lightweight list of users */ {
    users: {
      id: number;
      email: string;
      fullName?: string;
      isActive?: boolean;
    }[];
    total: number;
  };
export type GetLiteUsersApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Quick search string (matches name or email) */
  search?: string;
  /** Maximum number of results to return */
  limit?: number;
};
export type GetUserApiResponse = /** status 200 User details */ {
  /** Username (required) */
  username?: string;
  /** Poste ID for the user (optional) */
  posteId?: number | null;
  /** User email address (required) */
  email: string;
  /** Role ID for the user (required) */
  roleId: number;
  /** Role object for the user (optional) */
  role?: RoleOutputSchema | null;
  /** Array of permission IDs (optional) */
  permissions?: number[] | null;
  /** User profile information (optional) */
  profile?: {
    /** User first name (required) */
    firstName: string;
    /** User last name (required) */
    lastName: string;
    /** User phone number (optional) */
    phone?: string | null;
    /** User birthday (optional) */
    birthday?: string | null;
    /** User gender (optional) */
    gender?: ("Male" | "Female") | null;
    /** User biography (optional) */
    bio?: string | null;
  } | null;
  /** Unique identifier for the user */
  id: string;
  /** User creation timestamp */
  createdAt: string;
  /** User last update timestamp */
  updatedAt: string;
  /** Evaluations for this user */
  evaluations?: {
    evaluation_id: number;
    employee_id: number;
    skill_id: number;
    evaluation_date: string;
    score: number;
    level?: string | null;
    comments?: string | null;
    evaluation_type?:
      | ("oral" | "written" | "practical")
      | ("oral" | "written" | "practical");
    rater_type: "Self-assessment" | "Manager" | "Peer";
    evaluatedBy?: null;
    createdAt: string;
    updatedAt: string;
    skill?: {
      skill_id: number;
      skill_code: string;
      name: string;
      description?: string | null;
      priority: "low" | "medium" | "high";
      unit_id: number;
      comments?: string | null;
      awareness_description?: string | null;
      skilled_application_description?: string | null;
      management_description?: string | null;
      internal_reference?: string | null;
      external_reference?: string | null;
      createdAt: string;
      updatedAt: string;
    };
  }[];
};
export type GetUserApiArg = {
  userId: number;
  /** Filter by user type,in client api we dont need it,in agent api its agent by default if the endpoint doesnt send any query */
  type?: "user";
};
export type DeleteUserApiResponse =
  /** status 200 User deleted successfully */ {
    message: string;
  };
export type DeleteUserApiArg = {
  userId: number;
  force?: boolean;
  /** Filter by user type,in client api we dont need it,in agent api its agent by default if the endpoint doesnt send any query */
  type?: "user";
};
export type UpdateUserApiResponse =
  /** status 200 User updated successfully */ {
    data: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
    message: string;
  };
export type UpdateUserApiArg = {
  body: {
    /** Updated username (optional) */
    username?: string | null;
    /** Updated role ID (optional) */
    roleId?: number | null;
    /** User active status (optional) */
    isActive?: boolean | null;
    /** User profile information (optional) */
    profile?: {
      /** Updated first name (optional) */
      firstName?: string | null;
      /** Updated last name (optional) */
      lastName?: string | null;
      /** Updated phone number (optional) */
      phone?: string | null;
      /** Updated birthday (optional) */
      birthday?: string | null;
      /** Updated gender (optional) */
      gender?: ("Male" | "Female") | null;
      /** Updated biography (optional) */
      bio?: string | null;
      /** Profile picture URL (optional) */
      profilePic?: string | null;
      /** Updated address (optional) */
      addresse?: string | null;
    } | null;
  };
};
export type PutUsersUpdateByUserIdApiResponse =
  /** status 200 User updated successfully */ {
    message: string;
    user: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
  };
export type PutUsersUpdateByUserIdApiArg = {
  /** ID of the user to update */
  userId: string;
  /** Filter by user type,in client api we dont need it,in agent api its agent by default if the endpoint doesnt send any query */
  type?: "user";
  body: {
    /** Updated username (optional) */
    username?: string | null;
    /** Updated role ID (optional) */
    roleId?: number | null;
    /** User active status (optional) */
    isActive?: boolean | null;
    /** User profile information (optional) */
    profile?: {
      /** Updated first name (optional) */
      firstName?: string | null;
      /** Updated last name (optional) */
      lastName?: string | null;
      /** Updated phone number (optional) */
      phone?: string | null;
      /** Updated birthday (optional) */
      birthday?: string | null;
      /** Updated gender (optional) */
      gender?: ("Male" | "Female") | null;
      /** Updated biography (optional) */
      bio?: string | null;
      /** Profile picture URL (optional) */
      profilePic?: string | null;
      /** Updated address (optional) */
      addresse?: string | null;
    } | null;
  };
};
export type RefreshTokenApiResponse =
  /** status 200 Token refreshed successfully */ {
    message: string;
    user: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
  };
export type RefreshTokenApiArg = void;
export type LogoutApiResponse = /** status 200 Logged out successfully */ {
  message: string;
};
export type LogoutApiArg = void;
export type RequestPasswordResetApiResponse =
  /** status 200 Password reset email sent */ {
    message: string;
  };
export type RequestPasswordResetApiArg = {
  body: {
    email: string;
  };
};
export type ResetPasswordApiResponse =
  /** status 200 Password reset successful */ {
    message: string;
  };
export type ResetPasswordApiArg = {
  body: {
    token: string;
    newPassword: string;
  };
};
export type ChangePasswordApiResponse =
  /** status 200 Password changed successfully */ {
    message: string;
  };
export type ChangePasswordApiArg = {
  changePasswordSchema: ChangePasswordSchema;
};
export type GetPermissionsApiResponse = /** status 200 List of permissions */ {
  permissions: {
    /** The unique identifier of the permission */
    permission_id: number;
    /** The model this permission applies to */
    model: string;
    /** The action this permission allows */
    action: string;
    /** Information about who created the permission */
    createdBy?: {
      userType: string;
      id: string;
      email: string;
      username: string;
    };
  }[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
  };
};
export type GetPermissionsApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type AssignMultiplePermissionsApiResponse =
  /** status 200 Permissions assigned successfully */ {
    /** Success message */
    message: string;
    /** Updated user information with permissions */
    user: {
      id: string;
      username: string;
      email: string;
      permissions: {
        /** The unique identifier of the permission */
        permission_id: number;
        /** The model this permission applies to */
        model: string;
        /** The action this permission allows */
        action: string;
        /** Information about who created the permission */
        createdBy?: {
          userType: string;
          id: string;
          email: string;
          username: string;
        };
      }[];
    };
  };
export type AssignMultiplePermissionsApiArg = {
  userId: string;
  body: {
    permissionIds: number[];
  };
};
export type RemovePermissionApiResponse =
  /** status 200 Permission removed successfully */ {
    message: string;
    user: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
  };
export type RemovePermissionApiArg = {
  userId: string;
  permissionId: string;
};
export type GetPermissionSetsApiResponse =
  /** status 200 List of available permission sets */ {
    permissionSets: {
      name: string;
      description: string;
      modelActions: {
        [key: string]: string[];
      };
    }[];
    message?: string;
  };
export type GetPermissionSetsApiArg = void;
export type GetPostesApiResponse = /** status 200 List of postes */ {
  postes: PosteOutputSchema[];
};
export type GetPostesApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  /** If true, returns all postes without pagination. When used, the response pagination field may be null. */
  getAllWithoutPagination?: boolean;
};
export type CreatePosteApiResponse =
  /** status 201 Poste created successfully */ PosteOutputSchema;
export type CreatePosteApiArg = {
  posteCreateSchema: PosteCreateSchema;
};
export type CreatePosteWithRequirementsApiResponse =
  /** status 201 Poste created and requirements assigned successfully */ {
    poste: PosteOutputSchema;
    requirements?: any;
  };
export type CreatePosteWithRequirementsApiArg = {
  body: {
    /** Position name */
    name: string;
    /** Optional discipline id */
    discipline_id?: number | null;
    /** Requirement skills to assign to the created poste */
    requirements: {
      skill_id: number;
      min_score?: number;
      max_score?: number;
      required_level?: string;
    }[];
  };
};
export type GetPosteApiResponse =
  /** status 200 Poste details */ PosteOutputSchema;
export type GetPosteApiArg = {
  posteId: string;
};
export type UpdatePosteApiResponse =
  /** status 200 Poste updated successfully */ PosteOutputSchema;
export type UpdatePosteApiArg = {
  posteId: string;
  posteUpdateSchema: PosteUpdateSchema;
};
export type DeletePosteApiResponse =
  /** status 200 Poste deleted successfully */ {
    message: string;
  };
export type DeletePosteApiArg = {
  posteId: string;
};
export type AssignPosteToUserApiResponse =
  /** status 200 Poste assigned successfully */ {
    message: string;
    user: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
  };
export type AssignPosteToUserApiArg = {
  posteId: string;
  userId: string;
};
export type AssignPosteToDisciplineApiResponse =
  /** status 200 Poste assigned to discipline successfully */ {
    success: boolean;
    message: string;
    data: PosteOutputSchema;
  };
export type AssignPosteToDisciplineApiArg = {
  disciplineId: number;
  posteId: number;
};
export type GetRolesApiResponse = /** status 200 List of roles */ {
  roles: RoleOutputSchema[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
  };
};
export type GetRolesApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type GetRoleApiResponse = /** status 200 Role details */ {
  role: RoleOutputSchema;
};
export type GetRoleApiArg = {
  roleId: string;
};
export type UpdateRoleApiResponse =
  /** status 200 Role updated successfully */ {
    message: string;
    role: RoleOutputSchema;
  };
export type UpdateRoleApiArg = {
  roleId: string;
  roleUpdateSchema: RoleUpdateSchema;
};
export type DeleteRoleApiResponse =
  /** status 200 Role deleted successfully */ {
    message: string;
  };
export type DeleteRoleApiArg = {
  roleId: string;
};
export type AssignRolesToUsersApiResponse =
  /** status 200 Role assigned successfully */ {
    message: string;
    user: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
  };
export type AssignRolesToUsersApiArg = {
  body: {
    /** Array of user IDs to assign the role to */
    userIds: number[];
    /** ID of the role to assign */
    roleId: number;
  };
};
export type CreateRoleApiResponse =
  /** status 201 Role created successfully */ RoleOutputSchema;
export type CreateRoleApiArg = {
  roleCreateSchema: RoleCreateSchema;
};
export type AssignPermissionsToRoleApiResponse =
  /** status 200 Permissions assigned successfully */ {
    message: string;
    role: RoleOutputSchema;
  };
export type AssignPermissionsToRoleApiArg = {
  roleId: number;
  body: {
    permissionIds: number[];
  };
};
export type GetAuditLogsApiResponse =
  /** status 200 Paginated list of audit logs */ {
    data: {
      id: number;
      request_id?: string | null;
      actor_user_id?: number | null;
      action:
        | "CREATE"
        | "READ"
        | "UPDATE"
        | "DELETE"
        | "BULK_UPDATE"
        | "BULK_DELETE"
        | "OTHER";
      model_name: string;
      entity_id?: string | null;
      before?: null;
      after?: null;
      diff?: null;
      success: boolean;
      error_message?: string | null;
      route?: string | null;
      module?: string | null;
      created_at: string;
      user?: any;
    }[];
    pagination: {
      total: number;
      totalPages: number;
      currentPage: number;
      limit: number;
    };
  };
export type GetAuditLogsApiArg = {
  page?: number;
  limit?: number;
  sortBy?:
    | "created_at"
    | "model_name"
    | "action"
    | "actor_user_id"
    | "entity_id"
    | "module";
  sortOrder?: "ASC" | "DESC" | "asc" | "desc";
  modelName?: string;
  action?:
    | "CREATE"
    | "READ"
    | "UPDATE"
    | "DELETE"
    | "BULK_UPDATE"
    | "BULK_DELETE"
    | "OTHER";
  actorUserId?: number;
  entityId?: string;
  module?: string;
  success?: boolean;
  requestId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
};
export type GetAuditLogByIdApiResponse = /** status 200 Audit log details */ {
  data: {
    id: number;
    request_id?: string | null;
    actor_user_id?: number | null;
    action:
      | "CREATE"
      | "READ"
      | "UPDATE"
      | "DELETE"
      | "BULK_UPDATE"
      | "BULK_DELETE"
      | "OTHER";
    model_name: string;
    entity_id?: string | null;
    before?: null;
    after?: null;
    diff?: null;
    success: boolean;
    error_message?: string | null;
    route?: string | null;
    module?: string | null;
    created_at: string;
    user?: any;
  };
};
export type GetAuditLogByIdApiArg = {
  id: number;
};
export type DeleteAuditLogByIdApiResponse = /** status 200 Deletion result */ {
  message: string;
};
export type DeleteAuditLogByIdApiArg = {
  id: number;
};
export type BulkDeleteAuditLogsApiResponse =
  /** status 200 Bulk delete result */ {
    message: string;
    deleted: number;
  };
export type BulkDeleteAuditLogsApiArg = {
  body: {
    ids?: (number | string)[];
    filter: {
      ids?: (number | string)[];
      filter?: {
        model_name?: string;
        action?:
          | "CREATE"
          | "READ"
          | "UPDATE"
          | "DELETE"
          | "BULK_UPDATE"
          | "BULK_DELETE"
          | "OTHER";
        actor_user_id?: number;
        entity_id?: string;
        module?: string;
        success?: boolean;
        request_id?: string;
        date_from?: string;
        date_to?: string;
        search?: string;
      };
    };
  };
};
export type GetCompaniesApiResponse =
  /** status 200 List of companies with pagination */ {
    companies: {
      /** The name of the company */
      name: string;
      /** The sector of the company (e.g., 'finance' or 'healthcare') */
      sector: string;
      /** The address of the company */
      address?: string;
      /** The address of the company */
      website?: string;
      /** The category of the company */
      size?: string;
      /** The description of the company */
      description?: string;
      /** The first phone number of the company */
      phoneNumber1?: string;
      /** The first phone number of the company */
      phoneNumber2?: string;
      /** The email of the company */
      email1: string;
      /** The email of the company */
      email2?: string;
      /** Send this Value only to activate or desactivate a company */
      isActivated?: boolean;
      /** Send this Value only to archive a company */
      isArchived?: boolean;
      /** The document of the company */
      document?: object;
      /** The logo of the company */
      logo?: object;
      /** The unique identifier of the company (integer) */
      id: number;
    }[];
    pagination: {
      currentPage: number;
      rowsPerPage: number;
      total: number;
    };
  };
export type GetCompaniesApiArg = {
  isArchived?: boolean;
  /** Array of filter objects. Each filter can contain field name and array of values. For dates use createdAt with start and end dates. */
  filters?: object[];
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  search?: any;
};
export type CreateCompanyApiResponse =
  /** status 201 Company and admin user created successfully */ {
    company: {
      /** The name of the company */
      name: string;
      /** The sector of the company (e.g., 'finance' or 'healthcare') */
      sector: string;
      /** The address of the company */
      address?: string;
      /** The address of the company */
      website?: string;
      /** The category of the company */
      size?: string;
      /** The description of the company */
      description?: string;
      /** The first phone number of the company */
      phoneNumber1?: string;
      /** The first phone number of the company */
      phoneNumber2?: string;
      /** The email of the company */
      email1: string;
      /** The email of the company */
      email2?: string;
      /** Send this Value only to activate or desactivate a company */
      isActivated?: boolean;
      /** Send this Value only to archive a company */
      isArchived?: boolean;
      /** The document of the company */
      document?: object;
      /** The logo of the company */
      logo?: object;
      /** The unique identifier of the company (integer) */
      id: number;
    };
    user: {
      /** Username (required) */
      username?: string;
      /** Poste ID for the user (optional) */
      posteId?: number | null;
      /** User email address (required) */
      email: string;
      /** Role ID for the user (required) */
      roleId: number;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      /** User profile information (optional) */
      profile?: {
        /** User first name (required) */
        firstName: string;
        /** User last name (required) */
        lastName: string;
        /** User phone number (optional) */
        phone?: string | null;
        /** User birthday (optional) */
        birthday?: string | null;
        /** User gender (optional) */
        gender?: ("Male" | "Female") | null;
        /** User biography (optional) */
        bio?: string | null;
      } | null;
      /** Unique identifier for the user */
      id: string;
      /** User creation timestamp */
      createdAt: string;
      /** User last update timestamp */
      updatedAt: string;
      /** Evaluations for this user */
      evaluations?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string;
        score: number;
        level?: string | null;
        comments?: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical")
          | ("oral" | "written" | "practical");
        rater_type: "Self-assessment" | "Manager" | "Peer";
        evaluatedBy?: null;
        createdAt: string;
        updatedAt: string;
        skill?: {
          skill_id: number;
          skill_code: string;
          name: string;
          description?: string | null;
          priority: "low" | "medium" | "high";
          unit_id: number;
          comments?: string | null;
          awareness_description?: string | null;
          skilled_application_description?: string | null;
          management_description?: string | null;
          internal_reference?: string | null;
          external_reference?: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }[];
    };
  };
export type CreateCompanyApiArg = {
  body: {
    companyData: {
      /** The name of the company */
      name: string;
      /** The sector of the company (e.g., 'finance' or 'healthcare') */
      sector: string;
      /** The address of the company */
      address?: string;
      /** The address of the company */
      website?: string;
      /** The category of the company */
      size?: string;
      /** The description of the company */
      description?: string;
      /** The first phone number of the company */
      phoneNumber1?: string;
      /** The first phone number of the company */
      phoneNumber2?: string;
      /** The email of the company */
      email1: string;
      /** The email of the company */
      email2?: string;
      /** Send this Value only to activate or desactivate a company */
      isActivated?: boolean;
      /** Send this Value only to archive a company */
      isArchived?: boolean;
      /** The document of the company */
      document?: object;
      /** The logo of the company */
      logo?: object;
    };
    userData: {
      /** Username (required) */
      username?: string;
      /** Mot de passe explicite pour l'utilisateur (optionnel) */
      authPassword: string;
      /** User email address (required) */
      email: string;
      /** Role object for the user (optional) */
      role?: RoleOutputSchema | null;
      /** Array of permission IDs (optional) */
      permissions?: number[] | null;
      profile: {
        firstName: string;
        lastName: string;
        phone: string;
        gender: "Male" | "Female";
      };
    };
  };
};
export type GetCompanyApiResponse =
  /** status 200 Company details with paginated users */ {
    company: {
      /** The name of the company */
      name: string;
      /** The sector of the company (e.g., 'finance' or 'healthcare') */
      sector: string;
      /** The address of the company */
      address?: string;
      /** The address of the company */
      website?: string;
      /** The category of the company */
      size?: string;
      /** The description of the company */
      description?: string;
      /** The first phone number of the company */
      phoneNumber1?: string;
      /** The first phone number of the company */
      phoneNumber2?: string;
      /** The email of the company */
      email1: string;
      /** The email of the company */
      email2?: string;
      /** Send this Value only to activate or desactivate a company */
      isActivated?: boolean;
      /** Send this Value only to archive a company */
      isArchived?: boolean;
      /** The document of the company */
      document?: object;
      /** The logo of the company */
      logo?: object;
      /** The unique identifier of the company (integer) */
      id: number;
    };
    pagination: {
      currentPage: number;
      rowsPerPage: number;
      total: number;
    };
  };
export type GetCompanyApiArg = {
  isArchived?: boolean;
  companyId: string;
  /** Array of filter objects for users and profiles. Use profile. prefix for profile fields. */
  filters?: object[];
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  search?: any;
};
export type UpdateCompanyApiResponse =
  /** status 200 Company updated successfully */ {
    /** The name of the company */
    name: string;
    /** The sector of the company (e.g., 'finance' or 'healthcare') */
    sector: string;
    /** The address of the company */
    address?: string;
    /** The address of the company */
    website?: string;
    /** The category of the company */
    size?: string;
    /** The description of the company */
    description?: string;
    /** The first phone number of the company */
    phoneNumber1?: string;
    /** The first phone number of the company */
    phoneNumber2?: string;
    /** The email of the company */
    email1: string;
    /** The email of the company */
    email2?: string;
    /** Send this Value only to activate or desactivate a company */
    isActivated?: boolean;
    /** Send this Value only to archive a company */
    isArchived?: boolean;
    /** The document of the company */
    document?: object;
    /** The logo of the company */
    logo?: object;
    /** The unique identifier of the company (integer) */
    id: number;
  };
export type UpdateCompanyApiArg = {
  companyId: string;
  body: {
    /** The name of the company */
    name?: string;
    /** The type of the company */
    type?: "enterprise" | "startup";
    /** The address of the company */
    address?: string;
    /** Company website */
    website?: string;
    /** Company category */
    category?: string;
    /** Company description */
    description?: string;
    /** Primary phone number */
    phoneNumber1?: string;
    /** Secondary phone number */
    phoneNumber2?: string;
    /** Primary email */
    email1?: string;
    /** Secondary email */
    email2?: string;
  };
};
export type DeleteCompanyApiResponse =
  /** status 200 Company deleted successfully */ {
    message: string;
  };
export type DeleteCompanyApiArg = {
  companyId: string;
};
export type ArchiveCompanyApiResponse =
  /** status 200 Company archived successfully */ {
    /** The name of the company */
    name: string;
    /** The sector of the company (e.g., 'finance' or 'healthcare') */
    sector: string;
    /** The address of the company */
    address?: string;
    /** The address of the company */
    website?: string;
    /** The category of the company */
    size?: string;
    /** The description of the company */
    description?: string;
    /** The first phone number of the company */
    phoneNumber1?: string;
    /** The first phone number of the company */
    phoneNumber2?: string;
    /** The email of the company */
    email1: string;
    /** The email of the company */
    email2?: string;
    /** Send this Value only to activate or desactivate a company */
    isActivated?: boolean;
    /** Send this Value only to archive a company */
    isArchived?: boolean;
    /** The document of the company */
    document?: object;
    /** The logo of the company */
    logo?: object;
    /** The unique identifier of the company (integer) */
    id: number;
  };
export type ArchiveCompanyApiArg = {
  companyId: string;
  body: {
    archive: boolean;
  };
};
export type ActivateCompanyApiResponse =
  /** status 200 Company activated successfully */ {
    /** The name of the company */
    name: string;
    /** The sector of the company (e.g., 'finance' or 'healthcare') */
    sector: string;
    /** The address of the company */
    address?: string;
    /** The address of the company */
    website?: string;
    /** The category of the company */
    size?: string;
    /** The description of the company */
    description?: string;
    /** The first phone number of the company */
    phoneNumber1?: string;
    /** The first phone number of the company */
    phoneNumber2?: string;
    /** The email of the company */
    email1: string;
    /** The email of the company */
    email2?: string;
    /** Send this Value only to activate or desactivate a company */
    isActivated?: boolean;
    /** Send this Value only to archive a company */
    isArchived?: boolean;
    /** The document of the company */
    document?: object;
    /** The logo of the company */
    logo?: object;
    /** The unique identifier of the company (integer) */
    id: number;
  };
export type ActivateCompanyApiArg = {
  companyId: string;
  body: {
    activate: boolean;
  };
};
export type GetCompanyEmployeesApiResponse =
  /** status 200 List of company employees with pagination */ {
    employees: {
      count: number;
      rows: {
        /** Username (required) */
        username?: string;
        /** Poste ID for the user (optional) */
        posteId?: number | null;
        /** User email address (required) */
        email: string;
        /** Role ID for the user (required) */
        roleId: number;
        /** Role object for the user (optional) */
        role?: RoleOutputSchema | null;
        /** Array of permission IDs (optional) */
        permissions?: number[] | null;
        /** User profile information (optional) */
        profile?: {
          /** User first name (required) */
          firstName: string;
          /** User last name (required) */
          lastName: string;
          /** User phone number (optional) */
          phone?: string | null;
          /** User birthday (optional) */
          birthday?: string | null;
          /** User gender (optional) */
          gender?: ("Male" | "Female") | null;
          /** User biography (optional) */
          bio?: string | null;
        } | null;
        /** Unique identifier for the user */
        id: string;
        /** User creation timestamp */
        createdAt: string;
        /** User last update timestamp */
        updatedAt: string;
        /** Evaluations for this user */
        evaluations?: {
          evaluation_id: number;
          employee_id: number;
          skill_id: number;
          evaluation_date: string;
          score: number;
          level?: string | null;
          comments?: string | null;
          evaluation_type?:
            | ("oral" | "written" | "practical")
            | ("oral" | "written" | "practical");
          rater_type: "Self-assessment" | "Manager" | "Peer";
          evaluatedBy?: null;
          createdAt: string;
          updatedAt: string;
          skill?: {
            skill_id: number;
            skill_code: string;
            name: string;
            description?: string | null;
            priority: "low" | "medium" | "high";
            unit_id: number;
            comments?: string | null;
            awareness_description?: string | null;
            skilled_application_description?: string | null;
            management_description?: string | null;
            internal_reference?: string | null;
            external_reference?: string | null;
            createdAt: string;
            updatedAt: string;
          };
        }[];
      }[];
    };
    pagination: {
      currentPage: number;
      rowsPerPage: number;
      total: number;
    };
  };
export type GetCompanyEmployeesApiArg = {
  isArchived?: boolean;
  companyId: string;
  /** Array of filter objects for users and profiles. Use profile. prefix for profile fields. */
  filters?: object[];
  /** Search parameters */
  search?: string;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type GetDisciplinesApiResponse = /** status 200 List of disciplines */ {
  disciplines: {
    discipline_id: number;
    name: string;
    color?: string | null;
    /** Serialized JSON object with icon component reference: { type: string, name: string, props?: object } */
    icon?: string | null;
    createdAt: string;
    updatedAt: string;
    postes?: PosteOutputSchema[];
  }[];
  pagination?: {
    currentPage: number;
    rowsPerPage: number;
    total: number;
  };
};
export type GetDisciplinesApiArg = {
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  search?: any;
};
export type CreateDisciplineApiResponse =
  /** status 201 Discipline created successfully */ {
    discipline_id: number;
    name: string;
    color?: string | null;
    /** Serialized JSON object with icon component reference: { type: string, name: string, props?: object } */
    icon?: string | null;
    createdAt: string;
    updatedAt: string;
    postes?: PosteOutputSchema[];
  };
export type CreateDisciplineApiArg = {
  body: {
    name: string;
    color?: string;
    icon?: string;
  };
};
export type GetDisciplineByIdApiResponse =
  /** status 200 Discipline details */ {
    discipline_id: number;
    name: string;
    color?: string | null;
    /** Serialized JSON object with icon component reference: { type: string, name: string, props?: object } */
    icon?: string | null;
    createdAt: string;
    updatedAt: string;
    postes?: PosteOutputSchema[];
  };
export type GetDisciplineByIdApiArg = {
  disciplineId: number;
};
export type UpdateDisciplineApiResponse =
  /** status 200 Discipline updated successfully */ {
    discipline_id: number;
    name: string;
    color?: string | null;
    /** Serialized JSON object with icon component reference: { type: string, name: string, props?: object } */
    icon?: string | null;
    createdAt: string;
    updatedAt: string;
    postes?: PosteOutputSchema[];
  };
export type UpdateDisciplineApiArg = {
  disciplineId: number;
  body: {
    name?: string;
    color?: string;
    icon?: string;
  };
};
export type DeleteDisciplineApiResponse =
  /** status 200 Discipline deleted successfully */ {
    message: string;
  };
export type DeleteDisciplineApiArg = {
  disciplineId: number;
};
export type GetUnitsApiResponse = /** status 200 List of units */ {
  units: {
    unit_id: number;
    unit_code: string;
    specific_unit: string;
    discipline_id: number;
    createdAt: string;
    updatedAt: string;
    discipline?: {
      discipline_id: number;
      name: string;
      createdAt: string;
      updatedAt: string;
    };
  }[];
  pagination?: {
    currentPage: number;
    rowsPerPage: number;
    total: number;
  };
};
export type GetUnitsApiArg = {
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  search?: any;
  /** Filter by discipline ID */
  disciplineId?: number;
};
export type CreateUnitApiResponse =
  /** status 201 Unit created successfully */ {
    unit_id: number;
    unit_code: string;
    specific_unit: string;
    discipline_id: number;
    createdAt: string;
    updatedAt: string;
    discipline?: {
      discipline_id: number;
      name: string;
      createdAt: string;
      updatedAt: string;
    };
  };
export type CreateUnitApiArg = {
  body: {
    unit_code: string;
    specific_unit: string;
    discipline_id: number;
  };
};
export type AddSkillToUnitsApiResponse =
  /** status 200 Skill processed for target units */ {
    originalSkillId: number;
    createdCount: number;
    skippedExisting: number;
    skippedSameUnit: number;
    failedCount: number;
    results: {
      unitId: number;
      status: "created" | "skipped_exists" | "skipped_same_unit" | "failed";
      newSkillId?: number;
      existingSkillId?: number;
      error?: string;
    }[];
  };
export type AddSkillToUnitsApiArg = {
  body: {
    skillId: number;
    unitIds: number[];
  };
};
export type GetUnitByIdApiResponse = /** status 200 Unit details */ {
  unit_id: number;
  unit_code: string;
  specific_unit: string;
  discipline_id: number;
  createdAt: string;
  updatedAt: string;
  discipline?: {
    discipline_id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
};
export type GetUnitByIdApiArg = {
  unitId: number;
};
export type UpdateUnitApiResponse =
  /** status 200 Unit updated successfully */ {
    unit_id: number;
    unit_code: string;
    specific_unit: string;
    discipline_id: number;
    createdAt: string;
    updatedAt: string;
    discipline?: {
      discipline_id: number;
      name: string;
      createdAt: string;
      updatedAt: string;
    };
  };
export type UpdateUnitApiArg = {
  unitId: number;
  body: {
    unit_code?: string;
    specific_unit?: string;
    discipline_id?: number;
  };
};
export type DeleteUnitApiResponse =
  /** status 200 Unit deleted successfully */ {
    message: string;
  };
export type DeleteUnitApiArg = {
  unitId: number;
};
export type GetUnitsByDisciplineApiResponse =
  /** status 200 List of units for a discipline */ {
    units: {
      unit_id: number;
      unit_code: string;
      specific_unit: string;
      discipline_id: number;
      createdAt: string;
      updatedAt: string;
      discipline?: {
        discipline_id: number;
        name: string;
        createdAt: string;
        updatedAt: string;
      };
    }[];
    pagination?: {
      currentPage: number;
      rowsPerPage: number;
      total: number;
    };
  };
export type GetUnitsByDisciplineApiArg = {
  disciplineId: number;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  search?: any;
};
export type GetSkillsApiResponse = /** status 200 List of skills */ {
  skills: {
    skill_id: number;
    skill_code: string;
    name: string;
    description: string | null;
    priority: "low" | "medium" | "high";
    unit_id: number;
    comments: string | null;
    awareness_description?: string | null;
    skilled_application_description?: string | null;
    management_description?: string | null;
    internal_reference?: string | null;
    external_reference?: string | null;
    createdAt: string;
    updatedAt: string;
    unit?: {
      unit_id: number;
      unit_code: string;
      specific_unit: string;
      discipline_id: number;
      createdAt: string;
      updatedAt: string;
    };
  }[];
  pagination?: {
    currentPage: number;
    rowsPerPage: number;
    total: number;
  };
};
export type GetSkillsApiArg = {
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  search?: any;
  /** Filter by unit ID */
  unitId?: number;
  /** Filter by priority level */
  priority?: "low" | "medium" | "high";
  /** If true, returns all skills without pagination. Response pagination field may be null. */
  getAllWithoutPagination?: boolean;
};
export type CreateSkillApiResponse =
  /** status 201 Skill created successfully */ {
    skill_id: number;
    skill_code: string;
    name: string;
    description: string | null;
    priority: "low" | "medium" | "high";
    unit_id: number;
    comments: string | null;
    awareness_description?: string | null;
    skilled_application_description?: string | null;
    management_description?: string | null;
    internal_reference?: string | null;
    external_reference?: string | null;
    createdAt: string;
    updatedAt: string;
    unit?: {
      unit_id: number;
      unit_code: string;
      specific_unit: string;
      discipline_id: number;
      createdAt: string;
      updatedAt: string;
    };
  };
export type CreateSkillApiArg = {
  body: {
    skill_code: string;
    name: string;
    description?: string;
    priority: "low" | "medium" | "high";
    unit_id: number;
    comments?: string;
    awareness_description?: string;
    skilled_application_description?: string;
    management_description?: string;
    internal_reference?: string;
    external_reference?: string;
  };
};
export type GetSkillByIdApiResponse = /** status 200 Skill details */ {
  skill_id: number;
  skill_code: string;
  name: string;
  description: string | null;
  priority: "low" | "medium" | "high";
  unit_id: number;
  comments: string | null;
  awareness_description?: string | null;
  skilled_application_description?: string | null;
  management_description?: string | null;
  internal_reference?: string | null;
  external_reference?: string | null;
  createdAt: string;
  updatedAt: string;
  unit?: {
    unit_id: number;
    unit_code: string;
    specific_unit: string;
    discipline_id: number;
    createdAt: string;
    updatedAt: string;
  };
};
export type GetSkillByIdApiArg = {
  skillId: number;
};
export type UpdateSkillApiResponse =
  /** status 200 Skill updated successfully */ {
    skill_id: number;
    skill_code: string;
    name: string;
    description: string | null;
    priority: "low" | "medium" | "high";
    unit_id: number;
    comments: string | null;
    awareness_description?: string | null;
    skilled_application_description?: string | null;
    management_description?: string | null;
    internal_reference?: string | null;
    external_reference?: string | null;
    createdAt: string;
    updatedAt: string;
    unit?: {
      unit_id: number;
      unit_code: string;
      specific_unit: string;
      discipline_id: number;
      createdAt: string;
      updatedAt: string;
    };
  };
export type UpdateSkillApiArg = {
  skillId: number;
  body: {
    skill_code?: string;
    name?: string;
    description?: string;
    priority?: "low" | "medium" | "high";
    unit_id?: number;
    comments?: string;
    awareness_description?: string;
    skilled_application_description?: string;
    management_description?: string;
    internal_reference?: string;
    external_reference?: string;
  };
};
export type DeleteSkillApiResponse =
  /** status 200 Skill deleted successfully */ {
    message: string;
  };
export type DeleteSkillApiArg = {
  skillId: number;
};
export type GetSkillsByUnitApiResponse =
  /** status 200 List of skills for a unit */ {
    skills: {
      skill_id: number;
      skill_code: string;
      name: string;
      description: string | null;
      priority: "low" | "medium" | "high";
      unit_id: number;
      comments: string | null;
      awareness_description?: string | null;
      skilled_application_description?: string | null;
      management_description?: string | null;
      internal_reference?: string | null;
      external_reference?: string | null;
      createdAt: string;
      updatedAt: string;
      unit?: {
        unit_id: number;
        unit_code: string;
        specific_unit: string;
        discipline_id: number;
        createdAt: string;
        updatedAt: string;
      };
    }[];
    pagination?: {
      currentPage: number;
      rowsPerPage: number;
      total: number;
    };
  };
export type GetSkillsByUnitApiArg = {
  unitId: number;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
  search?: any;
  /** Filter by priority level */
  priority?: "low" | "medium" | "high";
};
export type GetAllEvaluationsApiResponse =
  /** status 200 List of evaluations */ {
    evaluations: {
      evaluation_id: number;
      employee_id: number;
      skill_id: number;
      evaluation_date: string | string;
      expiry_duration: number;
      expiry_date: string | string;
      score: number;
      level?: string | null;
      comments: string | null;
      evaluation_type?:
        | ("oral" | "written" | "practical" | "observation")
        | ("oral" | "written" | "practical" | "observation");
      rater_type_id?: number;
      /** User who evaluated the employee */
      evaluatedBy?: {
        id: string;
        email: string;
        username: string;
        userType: string;
      } | null;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
  };
export type GetAllEvaluationsApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type CreateEvaluationApiResponse =
  /** status 201 Evaluation created successfully */ {
    evaluation_id: number;
    employee_id: number;
    skill_id: number;
    evaluation_date: string | string;
    expiry_duration: number;
    expiry_date: string | string;
    score: number;
    level?: string | null;
    comments: string | null;
    evaluation_type?:
      | ("oral" | "written" | "practical" | "observation")
      | ("oral" | "written" | "practical" | "observation");
    rater_type_id?: number;
    /** User who evaluated the employee */
    evaluatedBy?: {
      id: string;
      email: string;
      username: string;
      userType: string;
    } | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
export type CreateEvaluationApiArg = {
  body: {
    employee_id: number;
    skill_id: number;
    evaluation_date: string | string;
    expiry_duration?: number;
    expiry_date?: string | string | null;
    score: number;
    level?: string;
    comments?: string;
    evaluation_type?: "oral" | "written" | "practical" | "observation";
    rater_type_id: number;
  };
};
export type BulkCreateEvaluationsApiResponse =
  /** status 201 Evaluations created successfully */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      createdCount: number;
      updatedCount: number;
      skippedCount: number;
      totalProcessed: number;
      errors: {
        employeeRef?: any;
        skillRef?: any;
        reason: string;
      }[];
      createdItems?: {
        evaluation_id: number;
        employee_id: number;
        skill_id: number;
        evaluation_date: string | string;
        expiry_duration: number;
        expiry_date: string | string;
        score: number;
        level?: string | null;
        comments: string | null;
        evaluation_type?:
          | ("oral" | "written" | "practical" | "observation")
          | ("oral" | "written" | "practical" | "observation");
        rater_type_id?: number;
        /** User who evaluated the employee */
        evaluatedBy?: {
          id: string;
          email: string;
          username: string;
          userType: string;
        } | null;
        createdAt: string | string;
        updatedAt: string | string;
      }[];
    };
  };
export type BulkCreateEvaluationsApiArg = {
  body: {
    employee_id: number;
    skills: {
      skill_id: string | number;
      score?: number;
      level?: number | string;
      evaluation_date?: string | string;
      evaluation_type?: "oral" | "written" | "practical";
      rater_type_id?: number;
      comments?: string;
    }[];
  }[];
};
export type GetEvaluationApiResponse = /** status 200 Evaluation details */ {
  evaluation_id: number;
  employee_id: number;
  skill_id: number;
  evaluation_date: string | string;
  expiry_duration: number;
  expiry_date: string | string;
  score: number;
  level?: string | null;
  comments: string | null;
  evaluation_type?:
    | ("oral" | "written" | "practical" | "observation")
    | ("oral" | "written" | "practical" | "observation");
  rater_type_id?: number;
  /** User who evaluated the employee */
  evaluatedBy?: {
    id: string;
    email: string;
    username: string;
    userType: string;
  } | null;
  createdAt: string | string;
  updatedAt: string | string;
};
export type GetEvaluationApiArg = {
  evaluationId: number;
};
export type UpdateEvaluationApiResponse =
  /** status 200 Evaluation updated successfully */ {
    evaluation_id: number;
    employee_id: number;
    skill_id: number;
    evaluation_date: string | string;
    expiry_duration: number;
    expiry_date: string | string;
    score: number;
    level?: string | null;
    comments: string | null;
    evaluation_type?:
      | ("oral" | "written" | "practical" | "observation")
      | ("oral" | "written" | "practical" | "observation");
    rater_type_id?: number;
    /** User who evaluated the employee */
    evaluatedBy?: {
      id: string;
      email: string;
      username: string;
      userType: string;
    } | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
export type UpdateEvaluationApiArg = {
  evaluationId: number;
  body: {
    employee_id?: number;
    skill_id?: number;
    evaluation_date?: string | string;
    expiry_duration?: number;
    expiry_date?: string | string | null;
    score?: number;
    level?: string;
    comments?: string;
    evaluation_type?: "oral" | "written" | "practical" | "observation";
    rater_type_id?: number;
  };
};
export type DeleteEvaluationApiResponse =
  /** status 200 Evaluation deleted successfully */ {
    message: string;
  };
export type DeleteEvaluationApiArg = {
  evaluationId: number;
};
export type GetEmployeeEvaluationsApiResponse =
  /** status 200 List of employee evaluations */ {
    evaluations: {
      evaluation_id: number;
      employee_id: number;
      skill_id: number;
      evaluation_date: string | string;
      expiry_duration: number;
      expiry_date: string | string;
      score: number;
      level?: string | null;
      comments: string | null;
      evaluation_type?:
        | ("oral" | "written" | "practical" | "observation")
        | ("oral" | "written" | "practical" | "observation");
      rater_type_id?: number;
      /** User who evaluated the employee */
      evaluatedBy?: {
        id: string;
        email: string;
        username: string;
        userType: string;
      } | null;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
  };
export type GetEmployeeEvaluationsApiArg = {
  employeeId: number;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type GetSkillEvaluationsApiResponse =
  /** status 200 List of skill evaluations */ {
    evaluations: {
      evaluation_id: number;
      employee_id: number;
      skill_id: number;
      evaluation_date: string | string;
      expiry_duration: number;
      expiry_date: string | string;
      score: number;
      level?: string | null;
      comments: string | null;
      evaluation_type?:
        | ("oral" | "written" | "practical" | "observation")
        | ("oral" | "written" | "practical" | "observation");
      rater_type_id?: number;
      /** User who evaluated the employee */
      evaluatedBy?: {
        id: string;
        email: string;
        username: string;
        userType: string;
      } | null;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
  };
export type GetSkillEvaluationsApiArg = {
  skillId: number;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type HasEvaluationsForEmployeeApiResponse = /** status 200  */ {
  success: boolean;
  hasEvaluations: boolean;
  count: number;
};
export type HasEvaluationsForEmployeeApiArg = {
  employeeId: number;
};
export type GetSkillPosteRequirementsApiResponse =
  /** status 200 List of skill poste requirements */ {
    requirements: {
      /** FK to skills.skill_id */
      skill_id: number;
      /** FK to Postes.posteId */
      poste_id: number;
      min_score?: number;
      max_score?: number;
      /** Optional level as string */
      required_level?: string;
      /** The unique identifier of the skill poste requirement */
      skill_poste_id: number;
      createdAt?: string;
      updatedAt?: string;
    }[];
  };
export type GetSkillPosteRequirementsApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type CreateSkillPosteRequirementApiResponse = /** status 201 Created */ {
  /** FK to skills.skill_id */
  skill_id: number;
  /** FK to Postes.posteId */
  poste_id: number;
  min_score?: number;
  max_score?: number;
  /** Optional level as string */
  required_level?: string;
  /** The unique identifier of the skill poste requirement */
  skill_poste_id: number;
  createdAt?: string;
  updatedAt?: string;
};
export type CreateSkillPosteRequirementApiArg = {
  body: {
    /** FK to skills.skill_id */
    skill_id: number;
    /** FK to Postes.posteId */
    poste_id: number;
    min_score?: number;
    max_score?: number;
    /** Optional level as string */
    required_level?: string;
  };
};
export type GetSkillPosteRequirementApiResponse =
  /** status 200 Requirement */ {
    /** FK to skills.skill_id */
    skill_id: number;
    /** FK to Postes.posteId */
    poste_id: number;
    min_score?: number;
    max_score?: number;
    /** Optional level as string */
    required_level?: string;
    /** The unique identifier of the skill poste requirement */
    skill_poste_id: number;
    createdAt?: string;
    updatedAt?: string;
  };
export type GetSkillPosteRequirementApiArg = {
  skillPosteId: number;
};
export type UpdateSkillPosteRequirementApiResponse = /** status 200 Updated */ {
  /** FK to skills.skill_id */
  skill_id: number;
  /** FK to Postes.posteId */
  poste_id: number;
  min_score?: number;
  max_score?: number;
  /** Optional level as string */
  required_level?: string;
  /** The unique identifier of the skill poste requirement */
  skill_poste_id: number;
  createdAt?: string;
  updatedAt?: string;
};
export type UpdateSkillPosteRequirementApiArg = {
  skillPosteId: number;
  body: {
    min_score?: number;
    max_score?: number;
    required_level?: string;
  };
};
export type DeleteSkillPosteRequirementApiResponse = /** status 200 Deleted */ {
  message: string;
};
export type DeleteSkillPosteRequirementApiArg = {
  skillPosteId: number;
};
export type GetRequirementsBySkillApiResponse =
  /** status 200 Requirements for skill */ {
    requirements: {
      /** FK to skills.skill_id */
      skill_id: number;
      /** FK to Postes.posteId */
      poste_id: number;
      min_score?: number;
      max_score?: number;
      /** Optional level as string */
      required_level?: string;
      /** The unique identifier of the skill poste requirement */
      skill_poste_id: number;
      createdAt?: string;
      updatedAt?: string;
    }[];
  };
export type GetRequirementsBySkillApiArg = {
  skillId: number;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type GetRequirementsByPosteApiResponse =
  /** status 200 Requirements for poste */ {
    requirements: {
      /** FK to skills.skill_id */
      skill_id: number;
      /** FK to Postes.posteId */
      poste_id: number;
      min_score?: number;
      max_score?: number;
      /** Optional level as string */
      required_level?: string;
      /** The unique identifier of the skill poste requirement */
      skill_poste_id: number;
      createdAt?: string;
      updatedAt?: string;
    }[];
  };
export type GetRequirementsByPosteApiArg = {
  posteId: number;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type AssignRequirementsToPosteApiResponse =
  /** status 200 Requirements assigned successfully */ {
    success?: boolean;
    message: string;
    data?: {
      /** FK to skills.skill_id */
      skill_id: number;
      /** FK to Postes.posteId */
      poste_id: number;
      min_score?: number;
      max_score?: number;
      /** Optional level as string */
      required_level?: string;
      /** The unique identifier of the skill poste requirement */
      skill_poste_id: number;
      createdAt?: string;
      updatedAt?: string;
    }[];
  };
export type AssignRequirementsToPosteApiArg = {
  posteId: number;
  body: {
    items: {
      skill_id: number;
      min_score?: number;
      max_score?: number;
      required_level?: string;
    }[];
  };
};
export type GetRaterTypesApiResponse = /** status 200 List of rater types */ {
  raterTypes: {
    id?: number;
    name: string;
    description?: string | null;
    createdAt?: string | string;
    updatedAt?: string | string;
  }[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
  };
};
export type GetRaterTypesApiArg = {
  /** Array of filter objects */
  filters?: object;
  /** Pagination parameters */
  pagination?: object;
  /** Sorting parameters */
  sorting?: object;
};
export type CreateRaterTypeApiResponse =
  /** status 201 Rater type created successfully */ {
    message: string;
    raterType: {
      id?: number;
      name: string;
      description?: string | null;
      createdAt?: string | string;
      updatedAt?: string | string;
    };
  };
export type CreateRaterTypeApiArg = {
  body: {
    name: string;
    description?: string | null;
  };
};
export type GetRaterTypeApiResponse = /** status 200 Rater type details */ {
  raterType: {
    id?: number;
    name: string;
    description?: string | null;
    createdAt?: string | string;
    updatedAt?: string | string;
  };
};
export type GetRaterTypeApiArg = {
  id: string;
};
export type UpdateRaterTypeApiResponse =
  /** status 200 Rater type updated successfully */ {
    message: string;
    raterType: {
      id?: number;
      name: string;
      description?: string | null;
      createdAt?: string | string;
      updatedAt?: string | string;
    };
  };
export type UpdateRaterTypeApiArg = {
  id: string;
  body: {
    name?: string;
    description?: string | null;
  };
};
export type DeleteRaterTypeApiResponse =
  /** status 200 Rater type deleted successfully */ {
    message: string;
  };
export type DeleteRaterTypeApiArg = {
  id: string;
};
export type ListPracticalEvidencesApiResponse =
  /** status 200 Evidences fetched */ {
    success: true;
    data: {
      evidences: {
        evidence_id: number;
        evaluation_id: number;
        evidence_type:
          | "image"
          | "video"
          | "document"
          | "checklist"
          | "simulation";
        evidence_url: string;
        metadata?: null;
        captured_at?: string | string | null;
        location?: string | null;
        status?: "pending" | "approved" | "rejected";
        reviewed_by?: number | null;
        reviewed_at?: string | string | null;
        review_comment?: string | null;
        createdAt: string | string;
        updatedAt: string | string;
      }[];
      pagination: {
        total: number;
        currentPage: number;
        rowsPerPage: number;
      };
    };
  };
export type ListPracticalEvidencesApiArg = {
  currentPage?: string;
  rowsPerPage?: string;
  column?: string;
  direction?: "asc" | "desc";
  evaluationId?: string;
  type?: "image" | "video" | "document" | "checklist" | "simulation";
  employeeId?: number;
};
export type CreatePracticalEvidenceApiResponse =
  /** status 201 Evidence created successfully */ {
    success: true;
    message: string;
    data: {
      evidence_id: number;
      evaluation_id: number;
      evidence_type:
        | "image"
        | "video"
        | "document"
        | "checklist"
        | "simulation";
      evidence_url: string;
      metadata?: null;
      captured_at?: string | string | null;
      location?: string | null;
      status?: "pending" | "approved" | "rejected";
      reviewed_by?: number | null;
      reviewed_at?: string | string | null;
      review_comment?: string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type CreatePracticalEvidenceApiArg = {
  body: {
    evaluation_id: number;
    evidence_type: "image" | "video" | "document" | "checklist" | "simulation";
    evidence_url: string;
    metadata?: any;
    captured_at?: string | string;
    location?: string;
  };
};
export type GetPracticalEvidenceApiResponse =
  /** status 200 Evidence fetched */ {
    success: true;
    data: {
      evidence_id: number;
      evaluation_id: number;
      evidence_type:
        | "image"
        | "video"
        | "document"
        | "checklist"
        | "simulation";
      evidence_url: string;
      metadata?: null;
      captured_at?: string | string | null;
      location?: string | null;
      status?: "pending" | "approved" | "rejected";
      reviewed_by?: number | null;
      reviewed_at?: string | string | null;
      review_comment?: string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type GetPracticalEvidenceApiArg = {
  evidenceId: string;
};
export type UpdatePracticalEvidenceApiResponse = /** status 200 Updated */ {
  success: true;
  message: string;
  data: {
    evidence_id: number;
    evaluation_id: number;
    evidence_type: "image" | "video" | "document" | "checklist" | "simulation";
    evidence_url: string;
    metadata?: null;
    captured_at?: string | string | null;
    location?: string | null;
    status?: "pending" | "approved" | "rejected";
    reviewed_by?: number | null;
    reviewed_at?: string | string | null;
    review_comment?: string | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type UpdatePracticalEvidenceApiArg = {
  evidenceId: string;
  body: {
    evidence_type?: string;
    evidence_url?: string;
    file?: any;
    metadata?: any;
    captured_at?: string | string;
    location?: string;
  };
};
export type DeletePracticalEvidenceApiResponse = /** status 200 Deleted */ {
  success: true;
  message: string;
};
export type DeletePracticalEvidenceApiArg = {
  evidenceId: string;
};
export type ListEvaluationEvidencesApiResponse =
  /** status 200 Evidences fetched */ {
    success: true;
    data: {
      evidences: {
        evidence_id: number;
        evaluation_id: number;
        evidence_type:
          | "image"
          | "video"
          | "document"
          | "checklist"
          | "simulation";
        evidence_url: string;
        metadata?: null;
        captured_at?: string | string | null;
        location?: string | null;
        status?: "pending" | "approved" | "rejected";
        reviewed_by?: number | null;
        reviewed_at?: string | string | null;
        review_comment?: string | null;
        createdAt: string | string;
        updatedAt: string | string;
      }[];
      pagination: {
        total: number;
        currentPage: number;
        rowsPerPage: number;
      };
    };
  };
export type ListEvaluationEvidencesApiArg = {
  evaluationId: string;
};
export type CreateEvaluationEvidenceApiResponse = /** status 201 Created */ {
  success: true;
  message: string;
  data: {
    evidence_id: number;
    evaluation_id: number;
    evidence_type: "image" | "video" | "document" | "checklist" | "simulation";
    evidence_url: string;
    metadata?: null;
    captured_at?: string | string | null;
    location?: string | null;
    status?: "pending" | "approved" | "rejected";
    reviewed_by?: number | null;
    reviewed_at?: string | string | null;
    review_comment?: string | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type CreateEvaluationEvidenceApiArg = {
  evaluationId: string;
  body: {
    evidence_type: "image" | "video" | "document" | "checklist" | "simulation";
    evidence_url: string;
    metadata?: any;
    captured_at?: string | string;
    location?: string;
  };
};
export type UploadEvaluationEvidenceApiResponse = /** status 201 Uploaded */ {
  success: true;
  message: string;
  data: {
    evidence_id: number;
    evaluation_id: number;
    evidence_type: "image" | "video" | "document" | "checklist" | "simulation";
    evidence_url: string;
    metadata?: null;
    captured_at?: string | string | null;
    location?: string | null;
    status?: "pending" | "approved" | "rejected";
    reviewed_by?: number | null;
    reviewed_at?: string | string | null;
    review_comment?: string | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type UploadEvaluationEvidenceApiArg = {
  evaluationId: string;
  body: {
    evidence_type: "image" | "video" | "document";
    /** JSON string or simple text */
    metadata?: string;
    captured_at?: string;
    location?: string;
    file: Blob;
  };
};
export type ApprovePracticalEvidenceApiResponse = /** status 200 Approved */ {
  success: true;
  message: string;
  data: {
    evidence_id: number;
    evaluation_id: number;
    evidence_type: "image" | "video" | "document" | "checklist" | "simulation";
    evidence_url: string;
    metadata?: null;
    captured_at?: string | string | null;
    location?: string | null;
    status?: "pending" | "approved" | "rejected";
    reviewed_by?: number | null;
    reviewed_at?: string | string | null;
    review_comment?: string | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type ApprovePracticalEvidenceApiArg = {
  evidenceId: string;
  body: {
    review_comment?: string;
  };
};
export type RejectPracticalEvidenceApiResponse = /** status 200 Rejected */ {
  success: true;
  message: string;
  data: {
    evidence_id: number;
    evaluation_id: number;
    evidence_type: "image" | "video" | "document" | "checklist" | "simulation";
    evidence_url: string;
    metadata?: null;
    captured_at?: string | string | null;
    location?: string | null;
    status?: "pending" | "approved" | "rejected";
    reviewed_by?: number | null;
    reviewed_at?: string | string | null;
    review_comment?: string | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type RejectPracticalEvidenceApiArg = {
  evidenceId: string;
  body: {
    review_comment?: string;
  };
};
export type DownloadPracticalEvidenceApiResponse = unknown;
export type DownloadPracticalEvidenceApiArg = {
  evidenceId: string;
};
export type GetQuizzesApiResponse = /** status 200 List of quizzes */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    quizzes: {
      quizId: number;
      createdBy?: number;
      posteId?: number | null;
      title: string;
      description?: string | null;
      timeLimit: number;
      maxAttempts: number;
      shuffleQuestions?: boolean;
      shuffleOptions?: boolean;
      isActive?: boolean;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
};
export type GetQuizzesApiArg = {
  page?: number;
  limit?: number;
  search?: string;
  createdBy?: number;
  posteId?: number;
  isActive?: boolean;
  minTimeLimit?: number;
  maxTimeLimit?: number;
  minMaxAttempts?: number;
  maxMaxAttempts?: number;
};
export type CreateQuizApiResponse =
  /** status 201 Quiz created successfully */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      quizId: number;
      createdBy?: number;
      posteId?: number | null;
      title: string;
      description?: string | null;
      timeLimit: number;
      maxAttempts: number;
      shuffleQuestions?: boolean;
      shuffleOptions?: boolean;
      isActive?: boolean;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type CreateQuizApiArg = {
  body: {
    createdBy?: number;
    posteId?: number | null;
    title: string;
    description?: string;
    timeLimit: number;
    maxAttempts: number;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    isActive?: boolean;
  };
};
export type GetQuizByIdApiResponse = /** status 200 Quiz details */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    quizId: number;
    createdBy?: number;
    posteId?: number | null;
    title: string;
    description?: string | null;
    timeLimit: number;
    maxAttempts: number;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    isActive?: boolean;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type GetQuizByIdApiArg = {
  id: number;
};
export type UpdateQuizApiResponse =
  /** status 200 Quiz updated successfully */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      quizId: number;
      createdBy?: number;
      posteId?: number | null;
      title: string;
      description?: string | null;
      timeLimit: number;
      maxAttempts: number;
      shuffleQuestions?: boolean;
      shuffleOptions?: boolean;
      isActive?: boolean;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type UpdateQuizApiArg = {
  id: number;
  body: {
    createdBy?: number;
    posteId?: number | null;
    title?: string;
    description?: string;
    timeLimit?: number;
    maxAttempts?: number;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    isActive?: boolean;
  };
};
export type DeleteQuizApiResponse =
  /** status 200 Quiz deleted successfully */ {
    success: true;
    message: string;
  };
export type DeleteQuizApiArg = {
  id: number;
};
export type CreateQuizAssignmentApiResponse =
  /** status 201 Assignment created */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      assignmentId: number;
      quizId: number;
      assigneeUserId: number;
      assignedByUserId: number;
      startedAttemptId?: number | null;
      status:
        | "pending"
        | "accepted"
        | "revoked"
        | "declined"
        | "expired"
        | "cancelled";
      note?: string | null;
      dueAt?: string | string | null;
      expiresAt?: string | string | null;
      acceptedAt?: string | string | null;
      revokedAt?: string | string | null;
      declinedAt?: string | string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type CreateQuizAssignmentApiArg = {
  body: {
    quizId: number;
    assigneeUserId: number;
    note?: string;
    dueAt?: string | string;
    expiresAt?: string | string;
  };
};
export type GetQuizAssignmentsApiResponse = /** status 200 Assignments list */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    assignments: {
      assignmentId: number;
      quizId: number;
      assigneeUserId: number;
      assignedByUserId: number;
      startedAttemptId?: number | null;
      status:
        | "pending"
        | "accepted"
        | "revoked"
        | "declined"
        | "expired"
        | "cancelled";
      note?: string | null;
      dueAt?: string | string | null;
      expiresAt?: string | string | null;
      acceptedAt?: string | string | null;
      revokedAt?: string | string | null;
      declinedAt?: string | string | null;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
};
export type GetQuizAssignmentsApiArg = {
  page?: number;
  limit?: number;
  quizId?: number;
  assigneeUserId?: number;
  assignedByUserId?: number;
  status?:
    | "pending"
    | "accepted"
    | "revoked"
    | "declined"
    | "expired"
    | "cancelled";
  dueBefore?: string;
  dueAfter?: string;
  expBefore?: string;
  expAfter?: string;
};
export type GetQuizAssignmentByIdApiResponse =
  /** status 200 Assignment details */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      assignmentId: number;
      quizId: number;
      assigneeUserId: number;
      assignedByUserId: number;
      startedAttemptId?: number | null;
      status:
        | "pending"
        | "accepted"
        | "revoked"
        | "declined"
        | "expired"
        | "cancelled";
      note?: string | null;
      dueAt?: string | string | null;
      expiresAt?: string | string | null;
      acceptedAt?: string | string | null;
      revokedAt?: string | string | null;
      declinedAt?: string | string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type GetQuizAssignmentByIdApiArg = {
  assignmentId: number;
};
export type UpdateQuizAssignmentApiResponse =
  /** status 200 Assignment updated */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      assignmentId: number;
      quizId: number;
      assigneeUserId: number;
      assignedByUserId: number;
      startedAttemptId?: number | null;
      status:
        | "pending"
        | "accepted"
        | "revoked"
        | "declined"
        | "expired"
        | "cancelled";
      note?: string | null;
      dueAt?: string | string | null;
      expiresAt?: string | string | null;
      acceptedAt?: string | string | null;
      revokedAt?: string | string | null;
      declinedAt?: string | string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type UpdateQuizAssignmentApiArg = {
  assignmentId: number;
  body: {
    note?: string;
    dueAt?: string | string;
    expiresAt?: string | string;
  };
};
export type DeleteQuizAssignmentApiResponse =
  /** status 200 Assignment deleted */ {
    success: true;
    message: string;
  };
export type DeleteQuizAssignmentApiArg = {
  assignmentId: number;
};
export type AcceptQuizAssignmentApiResponse =
  /** status 200 Accepted; attempt started or returned */ {
    success: true;
    data: {
      assignment?: any;
      attempt?: null;
    };
  };
export type AcceptQuizAssignmentApiArg = {
  assignmentId: number;
};
export type DeclineQuizAssignmentApiResponse =
  /** status 200 Assignment declined */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      assignmentId: number;
      quizId: number;
      assigneeUserId: number;
      assignedByUserId: number;
      startedAttemptId?: number | null;
      status:
        | "pending"
        | "accepted"
        | "revoked"
        | "declined"
        | "expired"
        | "cancelled";
      note?: string | null;
      dueAt?: string | string | null;
      expiresAt?: string | string | null;
      acceptedAt?: string | string | null;
      revokedAt?: string | string | null;
      declinedAt?: string | string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type DeclineQuizAssignmentApiArg = {
  assignmentId: number;
};
export type RevokeQuizAssignmentApiResponse =
  /** status 200 Assignment revoked */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      assignmentId: number;
      quizId: number;
      assigneeUserId: number;
      assignedByUserId: number;
      startedAttemptId?: number | null;
      status:
        | "pending"
        | "accepted"
        | "revoked"
        | "declined"
        | "expired"
        | "cancelled";
      note?: string | null;
      dueAt?: string | string | null;
      expiresAt?: string | string | null;
      acceptedAt?: string | string | null;
      revokedAt?: string | string | null;
      declinedAt?: string | string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type RevokeQuizAssignmentApiArg = {
  assignmentId: number;
};
export type GetQuizQuestionsApiResponse =
  /** status 200 Quiz questions with options and skills */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      quiz: {
        quizId: number;
        title: string;
        description: string | null;
        timeLimit: number;
        maxAttempts: number;
        passingScore: number | null;
      };
      questions: {
        questionId: number;
        questionText: string;
        questionType:
          | "multiple_choice"
          | "true_false"
          | "short_answer"
          | "essay";
        points: number;
        questionOrder: number;
        isRequired: boolean;
        skill: {
          skillId: number;
          name: string;
          skillCode: string;
          priority: "low" | "medium" | "high";
        } | null;
        options: {
          optionId: number;
          optionText: string;
          optionOrder: number;
          isCorrect: boolean;
        }[];
      }[];
      totalQuestions: number;
      totalPoints: number;
    };
  };
export type GetQuizQuestionsApiArg = {
  id: number;
};
export type GetQuizSkillsApiResponse =
  /** status 200 Quiz skills with question counts */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      quiz: {
        quizId: number;
        title: string;
        description: string | null;
      };
      skills: {
        skill_id: number;
        name: string;
        skill_code: string;
        description: string | null;
        priority: "low" | "medium" | "high";
        questionsCount: number;
      }[];
      totalSkills: number;
    };
  };
export type GetQuizSkillsApiArg = {
  id: number;
};
export type StartQuizAttemptApiResponse = /** status 201 Attempt started */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    attemptId: number;
    userId: number;
    quizId: number;
    startTime: string | string;
    endTime?: string | string | null;
    timeSpent?: number | null;
    score?: number | null;
    status: "in_progress" | "completed" | "expired";
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type StartQuizAttemptApiArg = {
  body: {
    quizId: number;
  };
};
export type SubmitQuizAttemptApiResponse = /** status 200 Attempt submitted */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    attemptId: number;
    userId: number;
    quizId: number;
    startTime: string | string;
    endTime?: string | string | null;
    timeSpent?: number | null;
    score?: number | null;
    status: "in_progress" | "completed" | "expired";
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type SubmitQuizAttemptApiArg = {
  attemptId: number;
};
export type EvaluateQuizAttemptApiResponse =
  /** status 200 Evaluation result */ {
    success: true;
    data: {
      totalScore: number;
    };
  };
export type EvaluateQuizAttemptApiArg = {
  attemptId: number;
};
export type GetResponsesForAttemptApiResponse =
  /** status 200 Responses list */ {
    success: true;
    data: {
      responseId: number;
      attemptId: number;
      questionId: number;
      optionId?: number | null;
      textResponse?: string | null;
      isCorrect?: boolean | null;
      pointsEarned?: number | null;
      createdAt: string | string;
      updatedAt: string | string;
      question?: any;
      option?: any;
    }[];
  };
export type GetResponsesForAttemptApiArg = {
  attemptId: number;
};
export type SaveAttemptResponseApiResponse =
  /** status 200 Saved response(s) for the question */ {
    success: true;
    data:
      | {
          responseId: number;
          attemptId: number;
          questionId: number;
          optionId?: number | null;
          textResponse?: string | null;
          isCorrect?: boolean | null;
          pointsEarned?: number | null;
          createdAt: string | string;
          updatedAt: string | string;
          question?: any;
          option?: any;
        }
      | {
          responseId: number;
          attemptId: number;
          questionId: number;
          optionId?: number | null;
          textResponse?: string | null;
          isCorrect?: boolean | null;
          pointsEarned?: number | null;
          createdAt: string | string;
          updatedAt: string | string;
          question?: any;
          option?: any;
        }[];
  };
export type SaveAttemptResponseApiArg = {
  attemptId: number;
  body: {
    questionId: number;
    optionId?: number;
    optionIds?: number[];
    textResponse?: string;
  };
};
export type GetAttemptsApiResponse = /** status 200 Attempts for the quiz */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    quizAttempts: {
      attemptId: number;
      userId: number;
      quizId: number;
      startTime: string | string;
      endTime?: string | string | null;
      timeSpent?: number | null;
      score?: number | null;
      status: "in_progress" | "completed" | "expired";
      createdAt: string | string;
      updatedAt: string | string;
    }[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
};
export type GetAttemptsApiArg = {
  quizId?: number;
  page?: number;
  limit?: number;
  userId?: number;
  status?: "in_progress" | "completed" | "expired";
  minScore?: number;
  maxScore?: number;
  minTimeSpent?: number;
  maxTimeSpent?: number;
};
export type UpdateAttemptResponseApiResponse =
  /** status 200 Updated response */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      responseId: number;
      attemptId: number;
      questionId: number;
      optionId?: number | null;
      textResponse?: string | null;
      isCorrect?: boolean | null;
      pointsEarned?: number | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type UpdateAttemptResponseApiArg = {
  responseId: number;
  body: {
    optionId?: number;
    textResponse?: string;
  };
};
export type GetAllQuizResponsesApiResponse = /** status 200 Responses list */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    quizResponses: {
      responseId: number;
      attemptId: number;
      questionId: number;
      optionId?: number | null;
      textResponse?: string | null;
      isCorrect?: boolean | null;
      pointsEarned?: number | null;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
};
export type GetAllQuizResponsesApiArg = {
  page?: number;
  limit?: number;
  quizId?: number;
  userId?: number;
  attemptId?: number;
  questionId?: number;
  isCorrect?: boolean;
  textResponse?: string;
};
export type CreateQuizQuestionApiResponse = /** status 201 Question created */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    questionId: number;
    quizId: number;
    skillId?: number | null;
    questionType:
      | "single_choice"
      | "multiple_choice"
      | "true_false"
      | "short_answer"
      | "essay";
    questionText: string;
    hint?: string | null;
    points: number;
    timeLimit?: number | null;
    order: number;
    isRequired?: boolean;
    explanation?: string | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type CreateQuizQuestionApiArg = {
  body: {
    quizId: number;
    skillId?: number;
    questionType:
      | "single_choice"
      | "multiple_choice"
      | "true_false"
      | "short_answer"
      | "essay";
    questionText: string;
    hint?: string;
    points: number;
    timeLimit?: number;
    order: number;
    isRequired?: boolean;
    explanation?: string;
  };
};
export type GetQuizzesQuestionsApiResponse = /** status 200 Questions list */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    quizQuestions: {
      questionId: number;
      quizId: number;
      skillId?: number | null;
      questionType:
        | "single_choice"
        | "multiple_choice"
        | "true_false"
        | "short_answer"
        | "essay";
      questionText: string;
      hint?: string | null;
      points: number;
      timeLimit?: number | null;
      order: number;
      isRequired?: boolean;
      explanation?: string | null;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
};
export type GetQuizzesQuestionsApiArg = {
  page?: number;
  limit?: number;
  quizId?: number;
  skillId?: number;
  questionType?:
    | "single_choice"
    | "multiple_choice"
    | "true_false"
    | "short_answer"
    | "essay";
  isRequired?: boolean;
  minPoints?: number;
  maxPoints?: number;
  search?: string;
};
export type GetQuizQuestionByIdApiResponse =
  /** status 200 Question details */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      questionId: number;
      quizId: number;
      skillId?: number | null;
      questionType:
        | "single_choice"
        | "multiple_choice"
        | "true_false"
        | "short_answer"
        | "essay";
      questionText: string;
      hint?: string | null;
      points: number;
      timeLimit?: number | null;
      order: number;
      isRequired?: boolean;
      explanation?: string | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type GetQuizQuestionByIdApiArg = {
  id: number;
};
export type UpdateQuizQuestionApiResponse = /** status 200 Question updated */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    questionId: number;
    quizId: number;
    skillId?: number | null;
    questionType:
      | "single_choice"
      | "multiple_choice"
      | "true_false"
      | "short_answer"
      | "essay";
    questionText: string;
    hint?: string | null;
    points: number;
    timeLimit?: number | null;
    order: number;
    isRequired?: boolean;
    explanation?: string | null;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type UpdateQuizQuestionApiArg = {
  id: number;
  body: {
    quizId?: number;
    skillId?: number;
    questionType?:
      | "single_choice"
      | "multiple_choice"
      | "true_false"
      | "short_answer"
      | "essay";
    questionText?: string;
    hint?: string;
    points?: number;
    timeLimit?: number;
    order?: number;
    isRequired?: boolean;
    explanation?: string;
  };
};
export type DeleteQuizQuestionApiResponse = /** status 200 Question deleted */ {
  success: true;
  message: string;
};
export type DeleteQuizQuestionApiArg = {
  id: number;
};
export type CreateQuestionOptionApiResponse = /** status 201 Option created */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    optionId: number;
    questionId: number;
    optionText: string;
    isCorrect: boolean;
    order: number;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type CreateQuestionOptionApiArg = {
  body: {
    questionId: number;
    optionText: string;
    isCorrect?: boolean;
    order: number;
  };
};
export type GetQuestionOptionsApiResponse = /** status 200 Options list */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    questionOptions: {
      optionId: number;
      questionId: number;
      optionText: string;
      isCorrect: boolean;
      order: number;
      createdAt: string | string;
      updatedAt: string | string;
    }[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
};
export type GetQuestionOptionsApiArg = {
  page?: number;
  limit?: number;
  questionId?: number;
  isCorrect?: boolean;
  search?: string;
};
export type GetQuestionOptionByIdApiResponse =
  /** status 200 Option details */ {
    success: true;
    message?: string;
    statusCode?: number;
    data: {
      optionId: number;
      questionId: number;
      optionText: string;
      isCorrect: boolean;
      order: number;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type GetQuestionOptionByIdApiArg = {
  id: number;
};
export type UpdateQuestionOptionApiResponse = /** status 200 Option updated */ {
  success: true;
  message?: string;
  statusCode?: number;
  data: {
    optionId: number;
    questionId: number;
    optionText: string;
    isCorrect: boolean;
    order: number;
    createdAt: string | string;
    updatedAt: string | string;
  };
};
export type UpdateQuestionOptionApiArg = {
  id: number;
  body: {
    optionText?: string;
    isCorrect?: boolean;
    order?: number;
  };
};
export type DeleteQuestionOptionApiResponse = /** status 200 Option deleted */ {
  success: true;
  message: string;
};
export type DeleteQuestionOptionApiArg = {
  id: number;
};
export type ListTrainingRecommendationsApiResponse =
  /** status 200 Recommendations fetched */ {
    success: true;
    total: number;
    totalPages: number;
    currentPage: number;
    recommendations: {
      recommendation_id: number;
      gap_id: number;
      courseId?: string | null;
      sessionId?: number | null;
      training_name: string;
      training_type: "course" | "workshop" | "on_job" | "certification";
      estimated_hours: number;
      priority: "high" | "medium" | "low";
      status: "pending" | "scheduled" | "completed";
      suggested_by?: number | null;
      suggested_at?: string;
      createdAt?: string;
      updatedAt?: string;
      gap?: {
        skill?: any;
        poste?: any;
      };
      course?: any;
      session?: any;
      suggestedBy?: {
        id: number;
        username: string;
        email: string;
      };
    }[];
  };
export type ListTrainingRecommendationsApiArg = {
  page?: string;
  limit?: string;
  gapId?: string;
  status?: "pending" | "scheduled" | "completed";
  priority?: "high" | "medium" | "low";
  trainingType?: "course" | "workshop" | "on_job" | "certification";
  courseId?: string;
  sessionId?: string;
};
export type CreateTrainingRecommendationApiResponse =
  /** status 201 Created */ {
    success: true;
    data: {
      recommendation_id: number;
      gap_id: number;
      courseId?: string | null;
      sessionId?: number | null;
      training_name: string;
      training_type: "course" | "workshop" | "on_job" | "certification";
      estimated_hours: number;
      priority: "high" | "medium" | "low";
      status: "pending" | "scheduled" | "completed";
      suggested_by?: number | null;
      suggested_at?: string;
      createdAt?: string;
      updatedAt?: string;
      gap?: {
        skill?: any;
        poste?: any;
      };
      course?: any;
      session?: any;
      suggestedBy?: {
        id: number;
        username: string;
        email: string;
      };
    };
  };
export type CreateTrainingRecommendationApiArg = {
  body: {
    gap_id: number;
    training_name: string;
    training_type: "course" | "workshop" | "on_job" | "certification";
    estimated_hours: number;
    priority?: "high" | "medium" | "low";
    status?: "pending" | "scheduled" | "completed";
    courseId?: string;
    sessionId?: number;
    suggested_by?: number;
    suggested_at?: string | string;
  };
};
export type GetTrainingRecommendationApiResponse = /** status 200 Fetched */ {
  success: true;
  data: {
    recommendation_id: number;
    gap_id: number;
    courseId?: string | null;
    sessionId?: number | null;
    training_name: string;
    training_type: "course" | "workshop" | "on_job" | "certification";
    estimated_hours: number;
    priority: "high" | "medium" | "low";
    status: "pending" | "scheduled" | "completed";
    suggested_by?: number | null;
    suggested_at?: string;
    createdAt?: string;
    updatedAt?: string;
    gap?: {
      skill?: any;
      poste?: any;
    };
    course?: any;
    session?: any;
    suggestedBy?: {
      id: number;
      username: string;
      email: string;
    };
  };
};
export type GetTrainingRecommendationApiArg = {
  recommendationId: number;
};
export type UpdateTrainingRecommendationApiResponse =
  /** status 200 Updated */ {
    success: true;
    data: {
      recommendation_id: number;
      gap_id: number;
      courseId?: string | null;
      sessionId?: number | null;
      training_name: string;
      training_type: "course" | "workshop" | "on_job" | "certification";
      estimated_hours: number;
      priority: "high" | "medium" | "low";
      status: "pending" | "scheduled" | "completed";
      suggested_by?: number | null;
      suggested_at?: string;
      createdAt?: string;
      updatedAt?: string;
      gap?: {
        skill?: any;
        poste?: any;
      };
      course?: any;
      session?: any;
      suggestedBy?: {
        id: number;
        username: string;
        email: string;
      };
    };
  };
export type UpdateTrainingRecommendationApiArg = {
  recommendationId: number;
  body: {
    training_name?: string;
    training_type?: "course" | "workshop" | "on_job" | "certification";
    estimated_hours?: number;
    priority?: "high" | "medium" | "low";
    status?: "pending" | "scheduled" | "completed";
    courseId?: string;
    sessionId?: number;
    suggested_by?: number;
    suggested_at?: string | string;
  };
};
export type DeleteTrainingRecommendationApiResponse =
  /** status 200 Deleted */ {
    success: true;
    message: string;
  };
export type DeleteTrainingRecommendationApiArg = {
  recommendationId: number;
};
export type ListCompetencyGapsForEmployeePosteApiResponse =
  /** status 200 Gaps with pagination */ {
    success: true;
    data: {
      gaps: {
        gap_id: number;
        employee_id: number;
        poste_id: number;
        skill_id: number;
        current_score: number;
        required_score: number;
        gap_value: number;
        status: "met" | "partial" | "critical";
        createdAt?: string;
        updatedAt?: string;
        skill?: any;
        poste?: any;
      }[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    };
  };
export type ListCompetencyGapsForEmployeePosteApiArg = {
  employeeId: number;
  posteId: number;
  page?: string;
  limit?: string;
  skillId?: string;
  status?: "met" | "partial" | "critical";
};
export type RecomputeCompetencyGapsForEmployeePosteApiResponse =
  /** status 200 Recompute result */ {
    success: true;
    message?: string;
    data: {
      employee_id: number;
      poste_id: number;
      skill_id: number;
      current_score: number;
      required_score: number;
      gap_value: number;
      status: "met" | "partial" | "critical";
    }[];
  };
export type RecomputeCompetencyGapsForEmployeePosteApiArg = {
  employeeId: number;
  posteId: number;
};
export type GetPosteCompetencyMatrixApiResponse =
  /** status 200 Competency matrix */ {
    success: true;
    data: {
      posteId: number;
      skills: {
        id: number;
        name: string;
        required_min: number | null;
        required_max: number | null;
      }[];
      employees: {
        id: number;
        name: string;
      }[];
      cells: {
        employeeId: number;
        skillId: number;
        current: number;
        required: number | null;
        gap: number | null;
        status:
          | ("met" | "partial" | "critical")
          | ("met" | "partial" | "critical");
      }[];
      meta: {
        /** Threshold applied per skill = thresholdPercent * (required_max - required_min). Default 0.25 */
        thresholdPercent: number;
      };
    };
  };
export type GetPosteCompetencyMatrixApiArg = {
  posteId: number;
  /** Comma-separated employee IDs to filter */
  employeeIds?: string;
  /** Deprecated: matrix no longer uses fallbacks; cells become null if requirements are missing. */
  fallbackRequiredMin?: number;
  /** Deprecated: matrix no longer uses fallbacks; cells become null if requirements are missing. */
  fallbackRequiredMax?: number;
  /** Filter to only return employees whose current scores meet or exceed all required skill levels */
  showAcceptedOnly?: boolean;
};
export type ListCompetencyReportsApiResponse =
  /** status 200 List of competency reports */ {
    success: true;
    total: number;
    totalPages: number;
    currentPage: number;
    reports: {
      report_id: number;
      report_type: "individual" | "team" | "department" | "position";
      subject_type: "employee" | "team" | "department" | "position";
      subject_id: number;
      data_snapshot?: any;
      generated_by?: number | null;
      generated_at?: string | null;
      pdf_url?: string | null;
      is_scheduled: boolean;
      schedule_frequency?:
        | ("weekly" | "monthly" | "quarterly")
        | ("weekly" | "monthly" | "quarterly");
      createdAt?: string;
      updatedAt?: string;
      generatedBy?: {
        id: number;
        username: string;
        email: string;
      };
    }[];
  };
export type ListCompetencyReportsApiArg = {
  page?: string;
  limit?: string;
  reportType?: "individual" | "team" | "department" | "position";
  subjectType?: "employee" | "team" | "department" | "position";
  subjectId?: string;
  isScheduled?: string;
  scheduleFrequency?: "weekly" | "monthly" | "quarterly";
};
export type CreateCompetencyReportApiResponse = /** status 201 Created */ {
  success: true;
  data: {
    report_id: number;
    report_type: "individual" | "team" | "department" | "position";
    subject_type: "employee" | "team" | "department" | "position";
    subject_id: number;
    data_snapshot?: any;
    generated_by?: number | null;
    generated_at?: string | null;
    pdf_url?: string | null;
    is_scheduled: boolean;
    schedule_frequency?:
      | ("weekly" | "monthly" | "quarterly")
      | ("weekly" | "monthly" | "quarterly");
    createdAt?: string;
    updatedAt?: string;
    generatedBy?: {
      id: number;
      username: string;
      email: string;
    };
  };
};
export type CreateCompetencyReportApiArg = {
  body: {
    report_type: "individual" | "team" | "department" | "position";
    subject_type: "employee" | "team" | "department" | "position";
    subject_id: number;
    data_snapshot?: any;
    generated_by?: number | null;
    generated_at?: string | string;
    pdf_url?: string | null;
    is_scheduled?: boolean;
    schedule_frequency?:
      | ("weekly" | "monthly" | "quarterly")
      | ("weekly" | "monthly" | "quarterly");
  };
};
export type GetCompetencyReportApiResponse = /** status 200 Fetched */ {
  success: true;
  data: {
    report_id: number;
    report_type: "individual" | "team" | "department" | "position";
    subject_type: "employee" | "team" | "department" | "position";
    subject_id: number;
    data_snapshot?: any;
    generated_by?: number | null;
    generated_at?: string | null;
    pdf_url?: string | null;
    is_scheduled: boolean;
    schedule_frequency?:
      | ("weekly" | "monthly" | "quarterly")
      | ("weekly" | "monthly" | "quarterly");
    createdAt?: string;
    updatedAt?: string;
    generatedBy?: {
      id: number;
      username: string;
      email: string;
    };
  };
};
export type GetCompetencyReportApiArg = {
  reportId: number;
};
export type UpdateCompetencyReportApiResponse = /** status 200 Updated */ {
  success: true;
  data: {
    report_id: number;
    report_type: "individual" | "team" | "department" | "position";
    subject_type: "employee" | "team" | "department" | "position";
    subject_id: number;
    data_snapshot?: any;
    generated_by?: number | null;
    generated_at?: string | null;
    pdf_url?: string | null;
    is_scheduled: boolean;
    schedule_frequency?:
      | ("weekly" | "monthly" | "quarterly")
      | ("weekly" | "monthly" | "quarterly");
    createdAt?: string;
    updatedAt?: string;
    generatedBy?: {
      id: number;
      username: string;
      email: string;
    };
  };
};
export type UpdateCompetencyReportApiArg = {
  reportId: number;
  body: {
    report_type?: "individual" | "team" | "department" | "position";
    subject_type?: "employee" | "team" | "department" | "position";
    subject_id?: number;
    data_snapshot?: any;
    generated_by?: number | null;
    generated_at?: string | string;
    pdf_url?: string | null;
    is_scheduled?: boolean;
    schedule_frequency?:
      | ("weekly" | "monthly" | "quarterly")
      | ("weekly" | "monthly" | "quarterly");
  };
};
export type DeleteCompetencyReportApiResponse = /** status 200 Deleted */ {
  success: true;
  message: string;
};
export type DeleteCompetencyReportApiArg = {
  reportId: number;
};
export type ListCourseSkillMappingsApiResponse =
  /** status 200 List of course-skill mappings */ {
    success: true;
    total: number;
    totalPages: number;
    currentPage: number;
    mappings: {
      id: number;
      courseId: string;
      skill_id: number;
      coverage_level?:
        | ("awareness" | "skilled_application" | "management")
        | ("awareness" | "skilled_application" | "management");
      recommended_min_score?: number | null;
      createdAt?: string;
      updatedAt?: string;
      course?: any;
      skill?: any;
    }[];
  };
export type ListCourseSkillMappingsApiArg = {
  page?: string;
  limit?: string;
  courseId?: string;
  skillId?: string;
  coverageLevel?: "awareness" | "skilled_application" | "management";
};
export type CreateCourseSkillMappingApiResponse = /** status 201 Created */ {
  success: true;
  data: {
    id: number;
    courseId: string;
    skill_id: number;
    coverage_level?:
      | ("awareness" | "skilled_application" | "management")
      | ("awareness" | "skilled_application" | "management");
    recommended_min_score?: number | null;
    createdAt?: string;
    updatedAt?: string;
    course?: any;
    skill?: any;
  };
};
export type CreateCourseSkillMappingApiArg = {
  body: {
    courseId: string;
    skill_id: number;
    coverage_level?: "awareness" | "skilled_application" | "management";
    recommended_min_score?: number;
  };
};
export type GetCourseSkillMappingApiResponse = /** status 200 Fetched */ {
  success: true;
  data: {
    id: number;
    courseId: string;
    skill_id: number;
    coverage_level?:
      | ("awareness" | "skilled_application" | "management")
      | ("awareness" | "skilled_application" | "management");
    recommended_min_score?: number | null;
    createdAt?: string;
    updatedAt?: string;
    course?: any;
    skill?: any;
  };
};
export type GetCourseSkillMappingApiArg = {
  mappingId: number;
};
export type UpdateCourseSkillMappingApiResponse = /** status 200 Updated */ {
  success: true;
  data: {
    id: number;
    courseId: string;
    skill_id: number;
    coverage_level?:
      | ("awareness" | "skilled_application" | "management")
      | ("awareness" | "skilled_application" | "management");
    recommended_min_score?: number | null;
    createdAt?: string;
    updatedAt?: string;
    course?: any;
    skill?: any;
  };
};
export type UpdateCourseSkillMappingApiArg = {
  mappingId: number;
  body: {
    coverage_level?: "awareness" | "skilled_application" | "management";
    recommended_min_score?: number;
  };
};
export type DeleteCourseSkillMappingApiResponse = /** status 200 Deleted */ {
  success: true;
  message: string;
};
export type DeleteCourseSkillMappingApiArg = {
  mappingId: number;
};
export type ListAllCertificationsApiResponse =
  /** status 200 List of certificates */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      certificates: {
        id: number;
        candidate_id: number;
        certification_type_id: number | null;
        name: string;
        issuer: string | null;
        issuedDate: string | string | null;
        expirationDate: string | string | null;
        certificateUrl: string | null;
        verificationCode: string | null;
        status: "active" | "expired" | "revoked";
        createdAt: string | string;
        updatedAt: string | string;
        candidate: {
          id: number;
          firstName: string;
          middleName: string | null;
          lastName: string;
          ceatedAt: string | string | null;
          user?: {
            id: number;
            email: string;
          };
        };
        certificationType: {
          id: number;
          name: string;
          description: string | null;
          category: string | null;
          issuer: string | null;
        } | null;
      }[];
      pagination: {
        totalItems: number;
        totalPages: number;
        currentPage: number;
        itemsPerPage: number;
      };
      stats: {
        totalCertificates: number;
        activeCertificates: number;
        expiredCertificates: number;
        revokedCertificates: number;
      };
    };
  };
export type ListAllCertificationsApiArg = {
  page?: number;
  limit?: number;
  certificationName?: string;
  certificationTypeId?: number;
  candidateId?: number;
  expirationState?: "all" | "active" | "expired" | "revoked";
  sortBy?: "createdAt" | "name" | "expirationDate" | "status" | "issuer";
  sortOrder?: "asc" | "desc";
};
export type CreateCertificateApiResponse =
  /** status 201 Certificate created successfully */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      id: number;
      candidate_id: number;
      certification_type_id: number | null;
      name: string;
      issuer: string | null;
      issuedDate: string | string | null;
      expirationDate: string | string | null;
      certificateUrl: string | null;
      verificationCode: string | null;
      status: "active" | "expired" | "revoked";
      createdAt: string | string;
      updatedAt: string | string;
      candidate: {
        id: number;
        firstName: string;
        middleName: string | null;
        lastName: string;
        ceatedAt: string | string | null;
        user?: {
          id: number;
        };
      };
      certificationType: {
        id: number;
        name: string;
        description: string | null;
        category: string | null;
        issuer: string | null;
      } | null;
    };
  };
export type CreateCertificateApiArg = {
  body: {
    candidate_id: number;
    certification_type_id?: number;
    name: string;
    issuer?: string;
    issuedDate?: string | string;
    expirationDate?: string | string;
    certificateUrl?: string;
    verificationCode?: string;
    status?: "active" | "expired" | "revoked";
  };
};
export type GetCertificateByIdApiResponse =
  /** status 200 Certificate details */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      id: number;
      candidate_id: number;
      certification_type_id: number | null;
      name: string;
      issuer: string | null;
      issuedDate: string | string | null;
      expirationDate: string | string | null;
      certificateUrl: string | null;
      verificationCode: string | null;
      status: "active" | "expired" | "revoked";
      createdAt: string | string;
      updatedAt: string | string;
      candidate: {
        id: number;
        firstName: string;
        middleName: string | null;
        lastName: string;
        ceatedAt: string | string | null;
        user?: {
          id: number;
        };
      };
      certificationType: {
        id: number;
        name: string;
        description: string | null;
        category: string | null;
        issuer: string | null;
      } | null;
    };
  };
export type GetCertificateByIdApiArg = {
  id: number;
};
export type UpdateCertificateApiResponse =
  /** status 200 Certificate updated successfully */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      id: number;
      candidate_id: number;
      certification_type_id: number | null;
      name: string;
      issuer: string | null;
      issuedDate: string | string | null;
      expirationDate: string | string | null;
      certificateUrl: string | null;
      verificationCode: string | null;
      status: "active" | "expired" | "revoked";
      createdAt: string | string;
      updatedAt: string | string;
      candidate: {
        id: number;
        firstName: string;
        middleName: string | null;
        lastName: string;
        ceatedAt: string | string | null;
        user?: {
          id: number;
        };
      };
      certificationType: {
        id: number;
        name: string;
        description: string | null;
        category: string | null;
        issuer: string | null;
      } | null;
    };
  };
export type UpdateCertificateApiArg = {
  id: number;
  body: {
    certification_type_id?: number;
    name?: string;
    issuer?: string;
    issuedDate?: string | string;
    expirationDate?: string | string;
    certificateUrl?: string;
    verificationCode?: string;
    status?: "active" | "expired" | "revoked";
  };
};
export type DeleteCertificateApiResponse = unknown;
export type DeleteCertificateApiArg = {
  id: number;
};
export type ListAllCertificationTypesApiResponse =
  /** status 200 List of certification types */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      certificationTypes: {
        id: number;
        name: string;
        description: string | null;
        category: string | null;
        issuer: string | null;
        defaultDuration: number | null;
        createdAt: string | string;
        updatedAt: string | string;
      }[];
      pagination: {
        totalItems: number;
        totalPages: number;
        currentPage: number;
        itemsPerPage: number;
      };
    };
  };
export type ListAllCertificationTypesApiArg = {
  page?: number;
  limit?: number;
  name?: string;
  category?: string;
  issuer?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};
export type CreateCertificationTypeApiResponse =
  /** status 201 Certification type created successfully */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      id: number;
      name: string;
      description: string | null;
      category: string | null;
      issuer: string | null;
      defaultDuration: number | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type CreateCertificationTypeApiArg = {
  body: {
    name: string;
    description?: string;
    defaultDuration?: number;
    issuer?: string;
    category?: string;
  };
};
export type GetCertificationTypeByIdApiResponse =
  /** status 200 Certification type details */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      id: number;
      name: string;
      description: string | null;
      category: string | null;
      issuer: string | null;
      defaultDuration: number | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type GetCertificationTypeByIdApiArg = {
  id: number;
};
export type UpdateCertificationTypeApiResponse =
  /** status 200 Certification type updated successfully */ {
    success: boolean;
    message?: string;
    statusCode?: number;
    data: {
      id: number;
      name: string;
      description: string | null;
      category: string | null;
      issuer: string | null;
      defaultDuration: number | null;
      createdAt: string | string;
      updatedAt: string | string;
    };
  };
export type UpdateCertificationTypeApiArg = {
  id: number;
  body: {
    name?: string;
    description?: string;
    defaultDuration?: number;
    issuer?: string;
    category?: string;
  };
};
export type DeleteCertificationTypeApiResponse = unknown;
export type DeleteCertificationTypeApiArg = {
  id: number;
};
export type RoleCreateSchema = {
  /** Role name */
  name: string;
  /** Role type (chaîne en MAJUSCULES, ex: ADMIN, SUPERADMIN, RECRUITER, MANAGER, INSTRUCTOR, CANDIDATE, EMPLOYEE) */
  type: string;
  /** Array of permission IDs */
  permissions?: number[];
  /** Company ID */
  companyId?: number;
};
export type RoleOutputSchema = RoleCreateSchema & {
  /** The unique identifier of the role */
  id: string;
  /** Role creation timestamp */
  createdAt: string;
  /** Role last update timestamp */
  updatedAt: string;
  /** User who created the role */
  createdBy?: {
    /** User ID */
    id: string;
    /** User email */
    email: string;
    /** Username */
    username: string;
    /** Type of user */
    userType: "RECRUITER" | "CANDIDATE";
  };
};
export type ChangePasswordSchema = {
  /** Current password (required) */
  currentPassword: string;
  /** New password (required, must contain uppercase, lowercase, number, and special character) */
  newPassword: string & string & string & string;
};
export type PosteCreateSchema = {
  /** Position name */
  name: string;
};
export type PosteOutputSchema = PosteCreateSchema & {
  /** The unique identifier of the poste */
  id: string;
  /** Position creation timestamp */
  createdAt: string;
  /** Position last update timestamp */
  updatedAt: string;
  /** User who created the position */
  createdBy?: {
    id: string;
    email: string;
    username: string;
    userType: string;
  };
  /** Users assigned to this position */
  users?: {
    id: string;
    username: string;
    email: string;
  }[];
  /** Required skills assigned to this poste */
  skillRequirements?: {
    skill_poste_id?: number;
    skill_id: number;
    min_score?: number | null;
    max_score?: number | null;
    required_level?: string | null;
    skill?: {
      skill_id: number;
      name: string;
      unit_id?: number;
    };
  }[];
};
export type PosteUpdateSchema = {
  /** Updated position name */
  name?: string;
};
export type RoleUpdateSchema = {
  /** Updated role name */
  name?: string;
};
export const {
  useLoginClientUserMutation,
  useCreateUserMutation,
  useGetUsersQuery,
  useGetLiteUsersQuery,
  useGetUserQuery,
  useDeleteUserMutation,
  useUpdateUserMutation,
  usePutUsersUpdateByUserIdMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useGetPermissionsQuery,
  useAssignMultiplePermissionsMutation,
  useRemovePermissionMutation,
  useGetPermissionSetsQuery,
  useGetPostesQuery,
  useCreatePosteMutation,
  useCreatePosteWithRequirementsMutation,
  useGetPosteQuery,
  useUpdatePosteMutation,
  useDeletePosteMutation,
  useAssignPosteToUserMutation,
  useAssignPosteToDisciplineMutation,
  useGetRolesQuery,
  useGetRoleQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAssignRolesToUsersMutation,
  useCreateRoleMutation,
  useAssignPermissionsToRoleMutation,
  useGetAuditLogsQuery,
  useGetAuditLogByIdQuery,
  useDeleteAuditLogByIdMutation,
  useBulkDeleteAuditLogsMutation,
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useGetCompanyQuery,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useArchiveCompanyMutation,
  useActivateCompanyMutation,
  useGetCompanyEmployeesQuery,
  useGetDisciplinesQuery,
  useCreateDisciplineMutation,
  useGetDisciplineByIdQuery,
  useUpdateDisciplineMutation,
  useDeleteDisciplineMutation,
  useGetUnitsQuery,
  useCreateUnitMutation,
  useAddSkillToUnitsMutation,
  useGetUnitByIdQuery,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
  useGetUnitsByDisciplineQuery,
  useGetSkillsQuery,
  useCreateSkillMutation,
  useGetSkillByIdQuery,
  useUpdateSkillMutation,
  useDeleteSkillMutation,
  useGetSkillsByUnitQuery,
  useGetAllEvaluationsQuery,
  useCreateEvaluationMutation,
  useBulkCreateEvaluationsMutation,
  useGetEvaluationQuery,
  useUpdateEvaluationMutation,
  useDeleteEvaluationMutation,
  useGetEmployeeEvaluationsQuery,
  useGetSkillEvaluationsQuery,
  useHasEvaluationsForEmployeeQuery,
  useGetSkillPosteRequirementsQuery,
  useCreateSkillPosteRequirementMutation,
  useGetSkillPosteRequirementQuery,
  useUpdateSkillPosteRequirementMutation,
  useDeleteSkillPosteRequirementMutation,
  useGetRequirementsBySkillQuery,
  useGetRequirementsByPosteQuery,
  useAssignRequirementsToPosteMutation,
  useGetRaterTypesQuery,
  useCreateRaterTypeMutation,
  useGetRaterTypeQuery,
  useUpdateRaterTypeMutation,
  useDeleteRaterTypeMutation,
  useListPracticalEvidencesQuery,
  useCreatePracticalEvidenceMutation,
  useGetPracticalEvidenceQuery,
  useUpdatePracticalEvidenceMutation,
  useDeletePracticalEvidenceMutation,
  useListEvaluationEvidencesQuery,
  useCreateEvaluationEvidenceMutation,
  useUploadEvaluationEvidenceMutation,
  useApprovePracticalEvidenceMutation,
  useRejectPracticalEvidenceMutation,
  useDownloadPracticalEvidenceQuery,
  useGetQuizzesQuery,
  useCreateQuizMutation,
  useGetQuizByIdQuery,
  useUpdateQuizMutation,
  useDeleteQuizMutation,
  useCreateQuizAssignmentMutation,
  useGetQuizAssignmentsQuery,
  useGetQuizAssignmentByIdQuery,
  useUpdateQuizAssignmentMutation,
  useDeleteQuizAssignmentMutation,
  useAcceptQuizAssignmentMutation,
  useDeclineQuizAssignmentMutation,
  useRevokeQuizAssignmentMutation,
  useGetQuizQuestionsQuery,
  useGetQuizSkillsQuery,
  useStartQuizAttemptMutation,
  useSubmitQuizAttemptMutation,
  useEvaluateQuizAttemptMutation,
  useGetResponsesForAttemptQuery,
  useSaveAttemptResponseMutation,
  useGetAttemptsQuery,
  useUpdateAttemptResponseMutation,
  useGetAllQuizResponsesQuery,
  useCreateQuizQuestionMutation,
  useGetQuizzesQuestionsQuery,
  useGetQuizQuestionByIdQuery,
  useUpdateQuizQuestionMutation,
  useDeleteQuizQuestionMutation,
  useCreateQuestionOptionMutation,
  useGetQuestionOptionsQuery,
  useGetQuestionOptionByIdQuery,
  useUpdateQuestionOptionMutation,
  useDeleteQuestionOptionMutation,
  useListTrainingRecommendationsQuery,
  useCreateTrainingRecommendationMutation,
  useGetTrainingRecommendationQuery,
  useUpdateTrainingRecommendationMutation,
  useDeleteTrainingRecommendationMutation,
  useListCompetencyGapsForEmployeePosteQuery,
  useRecomputeCompetencyGapsForEmployeePosteMutation,
  useGetPosteCompetencyMatrixQuery,
  useListCompetencyReportsQuery,
  useCreateCompetencyReportMutation,
  useGetCompetencyReportQuery,
  useUpdateCompetencyReportMutation,
  useDeleteCompetencyReportMutation,
  useListCourseSkillMappingsQuery,
  useCreateCourseSkillMappingMutation,
  useGetCourseSkillMappingQuery,
  useUpdateCourseSkillMappingMutation,
  useDeleteCourseSkillMappingMutation,
  useListAllCertificationsQuery,
  useCreateCertificateMutation,
  useGetCertificateByIdQuery,
  useUpdateCertificateMutation,
  useDeleteCertificateMutation,
  useListAllCertificationTypesQuery,
  useCreateCertificationTypeMutation,
  useGetCertificationTypeByIdQuery,
  useUpdateCertificationTypeMutation,
  useDeleteCertificationTypeMutation,
} = injectedRtkApi;
